/**
 * Dashboard — the main analytics / reporting surface (ADR-0108). Ported from
 * the reference `Foyer_Dashboard_CURRENT.jsx` lines 1670–2274 (plus the
 * `ConnectionPill` at 7641 and the `useEmptyState` helper at 270).
 *
 * Data: there is no analytics/aggregation API yet, so every figure is
 * clearly-marked SAMPLE data (the reference's synthetic numbers), gated by the
 * existing fresh-site + sample-traffic logic (`useSampleTraffic`,
 * `isFreshSite`). A fresh site with sample OFF renders a genuine empty state;
 * a real reporting API is a follow-up (ADR-0108).
 *
 * Charts: the reference used `recharts`, which is NOT a dependency and
 * CLAUDE.md §8 forbids adding one — TrafficChart/TopSkills are rendered as
 * inline SVG / CSS bars (matching the existing RevenueByAgent style) instead.
 *
 * Dry-run: the reference renders a dry-run banner off `useDryRun()`. That
 * context ships in ADR-0109; the banner reads `useDryRun()` from
 * `app/dry-run-context`.
 *
 * Naming: reference said "Foyer"; this port uses "Sill" (ADR-0013).
 */

import { useEffect, useMemo, useState } from 'react';
import type * as React from 'react';
import { motion } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Ban,
  Bot,
  DollarSign,
  Eye,
  Globe,
  Receipt,
  Search,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';

import type {
  ReportingBreakdowns,
  ReportingOverview,
  ReportingRange,
  ReportingTrafficSeries,
  Site,
  SiteId,
} from '@sill/types';
import {
  buildReadinessChecks,
  readinessVerdict,
  type ReadinessVerdict,
  type ReadinessVerdictKind,
  INBOUND_PROTOCOL_LABEL,
} from '@sill/types';
import { useReadinessData } from '../readiness/use-readiness-data.js';

import type { SetView, View } from '../../app/view-router.js';
import { AgencyRequestBanner } from './AgencyRequestBanner.js';
import { InstallUpdateBanner } from './InstallUpdateBanner.js';
import { SecurityCard } from './SecurityCard.js';
import { PlanUsageBanner } from '../billing/PlanUsageBanner.js';
import { useSites, useSampleTraffic } from '../../app/providers.js';
import { isFreshSite } from '../../lib/is-fresh-site.js';
import { isLiveSite } from '../../lib/site-state.js';
import { useC } from '../../lib/theme.js';
import { Card, EmptyStateCard, NumLabel, Pill, RelativeTime, SampleChip, StatusDot, Tooltip } from '../../components/atoms/index.js';
import { useDryRun } from '../../app/dry-run-context.js';
import { useApi } from '../../api/api-context.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { listEscalations, type EscalationItem } from '../../api/escalations.js';
import { formatMinor, formatRelativeTime } from '../../lib/format.js';
import type { MandateRecord } from '../mandates/types.js';
import { VisitorMixCard } from './VisitorMixCard.js';
import { Donut, SegmentBar } from '../../components/atoms/Donut.js';
import { CountryFlag } from '../../components/atoms/CountryFlag.js';
import { humanRecordDevice, humanRecordName, identityState } from '@sill/audit';

import { quotedName, type IdentityStateResult } from '../mandates/visit-identity.js';
import { VisitIdentityMark, VisitLogoGlyph } from '../mandates/VisitIdentityMarks.js';
import {
  friendlyActionLabel,
  friendlyRejectReason,
  botNameFromUserAgent,
  inferAgentFromUserAgent,
  outcomeLabel,
  settledTotalForHeadline,
  shortAgentDisplayName,
} from '../mandates/intent-label.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";

// `RangeId` is structurally identical to `@sill/types`'s `ReportingRange`
// (closed enum, same four tokens). Kept as a local alias because the
// dashboard had it pre-ADR-0115; the new ReportingAPI accepts the same
// shape verbatim.
type RangeId = ReportingRange;

// `useEmptyState` (reference line 270): a widget should render a genuine empty
// state when the site is fresh AND sample traffic is off.
function useEmptyState(): boolean {
  const { enabled } = useSampleTraffic();
  const { activeSite } = useSites();
  return isFreshSite(activeSite) && !enabled;
}

export interface DashboardProps {
  setView: SetView;
}

export function Dashboard({ setView }: DashboardProps): JSX.Element {
  const C = useC();
  const { activeSite } = useSites();
  const { enabled: sampleEnabled, setEnabled: setSampleEnabled } = useSampleTraffic();
  const { enabled: dryRunEnabled, setEnabled: setDryRunEnabled } = useDryRun();
  const live = activeSite ? isLiveSite(activeSite) : false;
  // ADR-0043 §4.1 — only `transactional_active` sites can sign payment
  // mandates; everything else is Discovery regardless of how long the
  // embed has been pinging. Drives the discovery banner (and the
  // ConnectionPill it links to) off the server's authoritative status
  // rather than embed-liveness.
  const transactional = activeSite?.status === 'transactional_active';
  const fresh = isFreshSite(activeSite);
  const [range, setRange] = useState<RangeId>('7d');
  const ranges: { id: RangeId; label: string }[] = [
    { id: '24h', label: '24h' },
    { id: '7d', label: '7d' },
    { id: '30d', label: '30d' },
    { id: '90d', label: '90d' },
    { id: '1y', label: '1y' },
  ];
  const currentRangeLabel = ranges.find((r) => r.id === range)?.label || '7d';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div className="min-w-0">
          <NumLabel n={1}>
            {activeSite?.name || 'Site'} · last {currentRangeLabel}
          </NumLabel>
          <h1 style={{ fontFamily: serifFont, fontSize: 'clamp(36px, 6vw, 52px)', lineHeight: 1, color: C.text, letterSpacing: -1.5 }}>
            AI agents on your site, <em>at a glance</em>.
          </h1>
        </div>
        {/*
          2026-06 founder mobile regression — the previous
          `flex-shrink-0` on this controls cluster forced it to keep
          its full natural width next to the title, which overflowed
          the page at ~375px (ConnectionPill + LIVE/SAMPLE + 4 range
          buttons together exceed the viewport). Reflow: the cluster
          drops to its own full-width row below the title on mobile
          (`w-full justify-end`), then sits inline with the title from
          `sm:` up (`sm:w-auto`). Shrinking is allowed so a long
          ConnectionPill string still wraps cleanly without bleeding.
        */}
        <div className="flex items-center gap-2 flex-wrap w-full justify-end sm:w-auto">
          {/* Founder 2026-10-20: the Discovery / Transaction-capable pill
              was redundant with the mode now shown in the site switcher
              and on the Sites list. The connection-tier context lives
              in Settings and the setup guide where it drives action. */}
          {/* 2026-06 cohesion pass — Sample / Live toggle COMPACTED into
              the header row. The old full-width banner stacked up to
              three deep before any metric showed; this segmented
              control sits next to the range buttons, with a tooltip
              that carries the verbose explanatory copy. */}
          {activeSite && (
            <Tooltip
              content={
                sampleEnabled
                  ? 'Sample data — every number, chart, list, and ring on this page is synthetic. Toggle off to see your real metrics.'
                  : fresh
                    ? 'No agent traffic yet. Toggle Sample to preview the dashboard before real agents arrive.'
                    : 'Live data — real metrics from your site. Toggle Sample for a preview-only view.'
              }
            >
              <div
                className="flex items-center"
                style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 2 }}
                role="group"
                aria-label="Data source"
                data-testid="data-source-toggle"
              >
                <button
                  onClick={() => setSampleEnabled(false)}
                  className="px-2.5 py-1.5"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    letterSpacing: 1,
                    fontWeight: 500,
                    color: !sampleEnabled ? C.text : C.muted,
                    background: !sampleEnabled ? C.surface2 : 'transparent',
                    borderRight: `1px solid ${C.borderSoft}`,
                  }}
                  aria-pressed={!sampleEnabled}
                >
                  LIVE
                </button>
                <button
                  onClick={() => setSampleEnabled(true)}
                  className="px-2.5 py-1.5"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    letterSpacing: 1,
                    fontWeight: 500,
                    color: sampleEnabled ? C.skyFg : C.muted,
                    background: sampleEnabled ? C.surface2 : 'transparent',
                  }}
                  aria-pressed={sampleEnabled}
                >
                  SAMPLE
                </button>
              </div>
            </Tooltip>
          )}
          <div className="flex items-center" style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 2 }}>
            {ranges.map((r, i) => (
              <button
                key={r.id}
                onClick={() => setRange(r.id)}
                className="px-2.5 py-1.5"
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  letterSpacing: 1,
                  fontWeight: 500,
                  color: range === r.id ? C.text : C.muted,
                  background: range === r.id ? C.surface2 : 'transparent',
                  borderRight: i < ranges.length - 1 ? `1px solid ${C.borderSoft}` : 'none',
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ADR-0263 §8 — pending agency-plan request banner. Fires the
          one-shot POST from the signed-in page load off the
          `sill_plan_intent` localStorage key written by the sign-up
          landing. Dismissible, session-local; posts nothing when the
          intent is absent or not an agency plan. */}
      <AgencyRequestBanner />

      {!transactional && activeSite && (
        <div className="p-3 mb-3 flex items-start gap-2 flex-wrap" style={{ background: C.amberDim, border: `1px solid ${C.amberFg}`, borderRadius: 2 }}>
          <AlertTriangle size={13} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-[260px]" style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
            <strong>Discovery only.</strong> This site has not reached the transactional tier yet. Agents can read your agent card and call read-only skills; signed mandates and connector dispatch are not enabled.
          </div>
          <button onClick={() => setView('settings')} className="flex-shrink-0 px-3 py-1.5" style={{ background: C.amber, color: C.ink, fontFamily: sansFont, fontSize: 12, fontWeight: 600, borderRadius: 2 }}>
            Open integrations →
          </button>
        </div>
      )}

      {/* ADR-0263 §7.2 (S8) — plan-usage banner (per site) for near /
          exceeded orders, exceeded page loads, exceeded assistant.
          Owner/admin only (role-gated inside the component). Hidden
          when `active: false` (every site `plan_source: 'none'`) or
          nothing qualifies. */}
      {activeSite && (
        <div className="mb-3">
          <PlanUsageBanner site_id={activeSite.site_id} domain={activeSite.domain} />
        </div>
      )}

      {/* ADR-0239 — Install update available banner. Renders ONLY when
          the site has an active outdated install AND the latest coverage
          scan reports a pending action (something is user-visibly
          uncovered). Dismissible per-site + per-version via localStorage. */}
      {activeSite && <InstallUpdateBanner site={activeSite} setView={setView} />}

      {/* Founder 2026-10-20 (option D) — compact Security card near the
          top of Overview. Reads threat-senders + blocking across a
          fixed 7-day window. Coral when any sender got a success
          response; quiet mint line otherwise. */}
      {activeSite && <SecurityCard site={activeSite} setView={setView} />}

      {/* 2026-06 cohesion pass — the sample/live banner has been
          replaced with a compact segmented control in the header row
          (see `data-source-toggle` above). The discovery + dry-run
          banners remain as they carry actionable next steps. */}

      {/* Dry-run banner (ADR-0109): renders when the dry-run context reports
          enabled. The toggle lives on the Guardrails page. */}
      {dryRunEnabled && activeSite && (
        <div className="p-3 mb-3 flex items-start gap-2 flex-wrap" style={{ background: C.mintDim, border: `1px solid ${C.mintFg}`, borderRadius: 2 }}>
          <Eye size={13} color={C.mintFg} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-[260px]" style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
            <strong>Dry-run mode.</strong> Rule changes are recorded but not enforced. Sill logs every "would have blocked" decision so you can review impact before going live.
          </div>
          <button onClick={() => setDryRunEnabled(false)} className="flex-shrink-0 px-3 py-1.5" style={{ background: 'transparent', color: C.mintFg, border: `1px solid ${C.mintFg}`, fontFamily: sansFont, fontSize: 12, fontWeight: 600, borderRadius: 2 }}>
            Exit dry-run
          </button>
        </div>
      )}

      <div className="mb-3" />

      <DashStatRow goTo={setView} range={range} />
      <LifecycleStrip range={range} />

      <div className="flex flex-col md:flex-row gap-6 mt-6">
        <div className="flex-1 min-w-0">
          {sampleEnabled ? <TrafficChart range={range} /> : <VisitorMixCard range={range} goTo={setView} />}
          <RevenueByAgent goTo={setView} range={range} />
        </div>
        <div className="flex-1 min-w-0 space-y-6">
          <VisibilityWidget goTo={setView} range={range} />
          <ActivityFeed goTo={setView} />
        </div>
      </div>
      {/* AI analytics row — AI Operators (grouped by company) + Policy
          Outcomes (decision breakdown). Both reuse the breakdowns hook
          so no additional round trips. */}
      <div className="flex flex-col md:flex-row gap-6 mt-6">
        <div className="flex-1 min-w-0">
          <AIOperatorsCard range={range} />
        </div>
        <div className="flex-1 min-w-0">
          <OutcomeBar range={range} />
        </div>
      </div>
      {/* ADR-0253 §A — How agents reach you: per-protocol visit mix. */}
      <div className="flex flex-col md:flex-row gap-6 mt-6">
        <div className="flex-1 min-w-0">
          <ProtocolMixCard range={range} goTo={setView} />
        </div>
        <div className="flex-1 min-w-0" />
      </div>
      {/* Bot intelligence row — Top Pages shows which URLs AI crawlers
          hit most; Search Terms shows what they queried. Both read the
          ADR-0203 breakdowns rollup, no additional round trips. */}
      <div className="flex flex-col md:flex-row gap-6 mt-6">
        <div className="flex-1 min-w-0">
          <TopPagesCard range={range} />
        </div>
        <div className="flex-1 min-w-0">
          <SearchTermsCard range={range} />
        </div>
      </div>
      {/* Transactional + governance row — Signed Transactions is the
          merchant's "what cleared this period" surface; Pending
          Resolutions is its HITL sibling (held mandates waiting for
          owner review). They sit together because they're the two
          surfaces an Owner checks first thing in the morning. */}
      <div className="flex flex-col md:flex-row gap-6 mt-6">
        <div className="flex-1 min-w-0">
          <RecentTransactions goTo={setView} />
        </div>
        <div className="flex-1 min-w-0">
          <PendingResolutions goTo={setView} />
        </div>
      </div>
      {/* Governance + top skills row — Blocked Attacks reads the
          `by_block_reason` breakdown (a governance signal: real
          rejected attempts grouped by reason). TopSkills sits next
          to it as the read-only counterpart (what agents actually
          do). */}
      <div className="flex flex-col md:flex-row gap-6 mt-6">
        <div className="flex-1 min-w-0">
          <BlockedAttacks range={range} />
        </div>
        <div className="flex-1 min-w-0">
          <TopSkills range={range} />
        </div>
      </div>
    </div>
  );
}

// `ConnectionPill` removed 2026-10-20 (founder): the DISCOVERY /
// TRANSACTION-CAPABLE pill restated the mode shown on the site
// switcher and Sites list, so a page-header copy was noise. The
// capability-tier semantics still drive Settings + the setup guide.

interface StatItem {
  label: string;
  value: string;
  delta: string;
  // `up: null` renders no arrow chip — used for the "—" (null delta)
  // first-traffic case where direction is undefined.
  up: boolean | null;
  sub: string;
  clickKey?: View;
  /**
   * When true, a DECREASE on this metric is a GOOD outcome — the chip
   * renders in lime, and an INCREASE renders in coral. Inverts the
   * default "up = good" coloring. Used for the FAILED / DISPUTED tile:
   * fewer failed settlements is better, so a `-25.0%` delta should
   * read as positive, not as the default coral. See ADR-0115 / the
   * 2026-06 dashboard cohesion pass for rationale.
   */
  lowerIsBetter?: boolean;
}

// ADR-0115 Slice A — sub-line copy per range. Stable across data
// arrival; the figure changes, not the label.
const PRIOR_WINDOW_SUB: Record<RangeId, string> = {
  '24h': 'vs prior 24h',
  '7d': 'vs prior 7d',
  '30d': 'vs prior 30d',
  '90d': 'vs prior 90d',
  '1y': 'vs prior 1y',
};

// ADR-0115 Slice A — render a `delta_pct` as the tile chip string.
// `null` (prior window was zero) → "—" (NEVER `+Inf%` — that's the
// central honesty invariant of the surface). Otherwise sign-prefixed
// at one decimal place, matching the reference's `+12.4%` shape.
//
// Returns the formatted string + an `up` boolean for the chip color
// (lime for ≥0, coral for <0). `null` → `up: null` so the renderer
// skips the chip entirely (no arrow, no color claim).
export function formatDeltaPct(delta_pct: number | null): {
  text: string;
  up: boolean | null;
} {
  if (delta_pct === null) {
    return { text: '—', up: null };
  }
  const sign = delta_pct >= 0 ? '+' : '';
  return { text: `${sign}${delta_pct.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`, up: delta_pct >= 0 };
}

/**
 * ADR-0115 Slice A — fetch the typed `ReportingOverview` for the
 * active site over the chosen window. Returns `null` until the first
 * fetch settles; an API failure (cross-tenant 404, network blip)
 * resets to `null` so the renderer falls back to the empty-row path,
 * preserving the existing honest-empty-state discipline (ADR-0108).
 *
 * The hook is gated behind `enabled` — the sample-on / fresh-site
 * paths short-circuit to the hard-coded sample row and we don't issue
 * a network call.
 */
function useReportingOverview(
  site_id: SiteId | undefined,
  range: RangeId,
  enabled: boolean,
): { overview: ReportingOverview | null; loading: boolean } {
  const api = useApi();
  const [overview, setOverview] = useState<ReportingOverview | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled || !site_id) {
      setOverview(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void api.reporting
      .getOverview(site_id, { range })
      .then((r) => {
        if (!cancelled) setOverview(r);
      })
      .catch(() => {
        // ADR-0108 honest-empty-state — a failure renders the empty
        // tiles, NEVER the sample numbers.
        if (!cancelled) setOverview(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, site_id, range, enabled]);

  return { overview, loading };
}

/**
 * ADR-0115 Slice B — fetch the typed `ReportingTrafficSeries` for the
 * active site. Same gating + error-fallback discipline as
 * `useReportingOverview`: sample-on / no site → no fetch; failure →
 * `null` (the empty-state branch renders).
 */
function useReportingTraffic(
  site_id: SiteId | undefined,
  range: RangeId,
  enabled: boolean,
): { traffic: ReportingTrafficSeries | null; loading: boolean } {
  const api = useApi();
  const [traffic, setTraffic] = useState<ReportingTrafficSeries | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled || !site_id) {
      setTraffic(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void api.reporting
      .getTraffic(site_id, { range })
      .then((r) => {
        if (!cancelled) setTraffic(r);
      })
      .catch(() => {
        if (!cancelled) setTraffic(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, site_id, range, enabled]);

  return { traffic, loading };
}

/**
 * ADR-0115 Slice C — fetch the typed `ReportingBreakdowns` composite.
 * One round trip drives three widgets: `LifecycleStrip`,
 * `RevenueByAgent`, `TopSkills`. Same gating + error-fallback
 * discipline as the other reporting hooks.
 */
function useReportingBreakdowns(
  site_id: SiteId | undefined,
  range: RangeId,
  enabled: boolean,
): { breakdowns: ReportingBreakdowns | null; loading: boolean } {
  const api = useApi();
  const [breakdowns, setBreakdowns] = useState<ReportingBreakdowns | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled || !site_id) {
      setBreakdowns(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void api.reporting
      .getBreakdowns(site_id, { range })
      .then((r) => {
        if (!cancelled) setBreakdowns(r);
      })
      .catch(() => {
        if (!cancelled) setBreakdowns(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, site_id, range, enabled]);

  return { breakdowns, loading };
}

// 2026-06 dashboard cohesion pass — project a typed `ReportingOverview`
// onto the three KPI tiles the row renders. The Visibility KPI has been
// REMOVED from the row to de-dupe with the visibility ring widget on the
// right column (a single visibility surface on the dashboard). The
// Disputes tile has been relabelled to FAILED / DISPUTED (the underlying
// SQL counts `outcome IN ('failed','disputed')` — there is no "pending"
// basis); it routes to the mandates view (not the unrelated HITL
// escalation queue) and is flagged `lowerIsBetter` so a decrease shows
// lime instead of coral.
function statsFromOverview(o: ReportingOverview, range: RangeId): StatItem[] {
  const calls = formatDeltaPct(o.agent_calls.delta_pct);
  const revenue = formatDeltaPct(o.revenue_minor.delta_pct);
  const disputes = formatDeltaPct(o.disputes_pending.delta_pct);
  const revenueValue =
    o.revenue_minor.currency !== null
      ? formatMinor(o.revenue_minor.total_minor, o.revenue_minor.currency)
      : formatMinor(o.revenue_minor.total_minor);
  return [
    {
      label: 'Revenue from agents',
      value: revenueValue,
      delta: revenue.text,
      up: revenue.up,
      sub: PRIOR_WINDOW_SUB[range],
    },
    {
      label: 'Agent calls',
      value: o.agent_calls.count.toLocaleString(),
      delta: calls.text,
      up: calls.up,
      sub: PRIOR_WINDOW_SUB[range],
    },
    {
      label: 'Failed / disputed',
      value: o.disputes_pending.count.toLocaleString(),
      delta: disputes.text,
      up: disputes.up,
      sub: o.disputes_pending.count === 0 ? 'none' : PRIOR_WINDOW_SUB[range],
      clickKey: 'mandates',
      lowerIsBetter: true,
    },
  ];
}

function DashStatRow({ goTo, range = '7d' }: { goTo: SetView; range?: RangeId }): JSX.Element {
  const C = useC();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const { activeSite } = useSites();
  const fresh = isFreshSite(activeSite);

  // 2026-06 dashboard cohesion pass — three KPIs: revenue, calls,
  // failed/disputed. The Visibility KPI has been folded into the
  // visibility ring widget (single surface). The Failed / Disputed
  // tile routes to mandates (the merchant's drill-in surface) and is
  // flagged `lowerIsBetter` so a decrease shows lime.
  const emptyRow: StatItem[] = [
    { label: 'Revenue from agents', value: '$0', delta: '—', up: null, sub: 'no traffic yet' },
    { label: 'Agent calls', value: '0', delta: '—', up: null, sub: 'no traffic yet' },
    { label: 'Failed / disputed', value: '0', delta: '—', up: null, sub: 'none', clickKey: 'mandates', lowerIsBetter: true },
  ];
  const emptyByRange: Record<RangeId, StatItem[]> = { '24h': emptyRow, '7d': emptyRow, '30d': emptyRow, '90d': emptyRow, '1y': emptyRow };

  // Sample preview path (fresh site + toggle-on) — keep the existing
  // hard-coded numbers verbatim so the sample-traffic toggle remains
  // a "what this page will look like" preview. Real-data wiring is
  // exclusively the sample-OFF branch.
  const sampleByRange: Record<RangeId, StatItem[]> = {
    '24h': [
      { label: 'Revenue from agents', value: '$1,142', delta: '+18.0%', up: true, sub: '21.8% of total' },
      { label: 'Agent calls', value: '847', delta: '+12.4%', up: true, sub: 'vs prior 24h' },
      { label: 'Failed / disputed', value: '1', delta: '0', up: true, sub: 'vs prior 24h', clickKey: 'mandates', lowerIsBetter: true },
    ],
    '7d': [
      { label: 'Revenue from agents', value: '$8,247', delta: '+62.1%', up: true, sub: '23.4% of total' },
      { label: 'Agent calls', value: '4,892', delta: '+38.4%', up: true, sub: 'vs prior 7d' },
      { label: 'Failed / disputed', value: '3', delta: '+1', up: false, sub: 'vs prior 7d', clickKey: 'mandates', lowerIsBetter: true },
    ],
    '30d': [
      { label: 'Revenue from agents', value: '$36,140', delta: '+71.2%', up: true, sub: '26.1% of total' },
      { label: 'Agent calls', value: '21,408', delta: '+52.7%', up: true, sub: 'vs prior 30d' },
      { label: 'Failed / disputed', value: '8', delta: '+3', up: false, sub: 'vs prior 30d', clickKey: 'mandates', lowerIsBetter: true },
    ],
    '90d': [
      { label: 'Revenue from agents', value: '$112,803', delta: '+184%', up: true, sub: '29.7% of total' },
      { label: 'Agent calls', value: '67,142', delta: '+128%', up: true, sub: 'vs prior 90d' },
      { label: 'Failed / disputed', value: '14', delta: '+5', up: false, sub: 'vs prior 90d', clickKey: 'mandates', lowerIsBetter: true },
    ],
    '1y': [
      { label: 'Revenue from agents', value: '$418,650', delta: '+212%', up: true, sub: '31.4% of total' },
      { label: 'Agent calls', value: '248,907', delta: '+167%', up: true, sub: 'vs prior 1y' },
      { label: 'Failed / disputed', value: '39', delta: '+11', up: false, sub: 'vs prior 1y', clickKey: 'mandates', lowerIsBetter: true },
    ],
  };

  // ADR-0115 Slice A — sample-off fetches the real overview; sample-on
  // skips the network and renders the hard-coded preview.
  const { overview } = useReportingOverview(
    activeSite?.site_id as SiteId | undefined,
    range,
    !sampleEnabled,
  );

  const useEmpty = fresh && !sampleEnabled;

  let stats: StatItem[];
  if (sampleEnabled) {
    stats = sampleByRange[range];
  } else if (useEmpty) {
    stats = emptyByRange[range];
  } else if (overview !== null) {
    stats = statsFromOverview(overview, range);
  } else {
    // Pre-load (and post-failure) → empty row. ADR-0108 honest-empty.
    stats = emptyByRange[range];
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
      {stats.map((s, i) => {
        // 2026-06 cohesion pass — `lowerIsBetter` (Failed / disputed)
        // inverts the chip coloring. A decrease (`up=false`) renders
        // lime; an increase (`up=true`) renders coral. The arrow
        // glyph still tracks the actual direction so the merchant
        // still knows whether the number went up or down at a glance.
        const goodOutcome = s.up !== null
          ? (s.lowerIsBetter === true ? !s.up : s.up)
          : null;
        return (
        <motion.div
          key={s.label}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.08 }}
          onClick={() => s.clickKey && goTo(s.clickKey)}
          className="p-4 sm:p-5 relative"
          style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 3, cursor: s.clickKey ? 'pointer' : 'default', opacity: useEmpty ? 0.7 : sampleEnabled ? 0.85 : 1 }}
          role={s.clickKey ? 'button' : undefined}
          tabIndex={s.clickKey ? 0 : undefined}
          onKeyDown={(e) => {
            if (!s.clickKey) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              goTo(s.clickKey);
            }
          }}
          aria-label={s.clickKey ? `${s.label} — open ${s.clickKey}` : undefined}
        >
          {sampleEnabled && !useEmpty && (
            <span
              style={{
                position: 'absolute',
                top: 6,
                right: 6,
                fontFamily: monoFont,
                fontSize: 8,
                letterSpacing: 1.5,
                fontWeight: 700,
                color: C.skyFg,
                background: C.skyDim,
                border: `1px solid ${C.skyFg}`,
                padding: '1px 4px',
                borderRadius: 2,
              }}
            >
              SAMPLE
            </span>
          )}
          <div className="flex items-center justify-between mb-3 gap-2">
            <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1.5 }}>{s.label.toUpperCase()}</span>
            {!useEmpty && s.up !== null && goodOutcome !== null && (
              <div
                className="flex items-center gap-1 px-1.5 py-0.5"
                style={{ background: goodOutcome ? C.limeDim : C.coralDim, border: `1px solid ${goodOutcome ? 'rgba(217,255,63,0.25)' : 'rgba(255,107,71,0.25)'}`, borderRadius: 2 }}
              >
                {s.up ? <ArrowUpRight size={10} color={goodOutcome ? C.limeFg : C.coralFg} /> : <ArrowDownRight size={10} color={goodOutcome ? C.limeFg : C.coralFg} />}
                <span style={{ fontFamily: monoFont, fontSize: 10, color: goodOutcome ? C.limeFg : C.coralFg }}>{s.delta}</span>
              </div>
            )}
            {/* ADR-0115 Slice A — `delta_pct: null` (prior window empty)
                renders a muted em-dash chip instead of `+Inf%`. Same
                affordance footprint as the up/down chip so the tile
                grid stays visually stable. */}
            {!useEmpty && s.up === null && s.delta === '—' && (
              <div
                className="flex items-center gap-1 px-1.5 py-0.5"
                style={{ background: 'transparent', border: `1px solid ${C.borderSoft}`, borderRadius: 2 }}
              >
                <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>{s.delta}</span>
              </div>
            )}
          </div>
          <div style={{ fontFamily: serifFont, fontSize: 28, lineHeight: 1, color: useEmpty ? C.muted : C.text, letterSpacing: -1, marginBottom: 6 }}>{s.value}</div>
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.dim }}>{s.sub}</div>
        </motion.div>
        );
      })}
    </div>
  );
}

// ADR-0115 Slice C — sample lifecycle counts, kept verbatim so the
// sample toggle is a stable "what this view will look like" preview.
// The shape mirrors `ReportingLifecycleCounts` exactly; widgets read
// the same six keys regardless of source.
const SAMPLE_LIFECYCLE_BY_RANGE: Record<RangeId, ReportingBreakdowns['lifecycle']> = {
  '24h': { discovered: 832, browsed: 365, carted: 104, paid: 83, fulfilled: 70, returned: 2 },
  '7d': { discovered: 4892, browsed: 2147, carted: 612, paid: 487, fulfilled: 412, returned: 12 },
  '30d': { discovered: 21525, browsed: 9447, carted: 2693, paid: 2143, fulfilled: 1813, returned: 53 },
  '90d': { discovered: 67020, browsed: 29413, carted: 8384, paid: 6672, fulfilled: 5644, returned: 164 },
  '1y': { discovered: 254880, browsed: 111800, carted: 31870, paid: 25360, fulfilled: 21450, returned: 624 },
};

function LifecycleStrip({ range = '7d' }: { range?: RangeId }): JSX.Element {
  const C = useC();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const { activeSite } = useSites();
  // ADR-0115 Slice C — real counts when sample is off (and a site is
  // active); fetch is skipped on sample-on so the network panel
  // doesn't show a wasted call.
  const { breakdowns, loading } = useReportingBreakdowns(
    activeSite?.site_id as SiteId | undefined,
    range,
    !sampleEnabled,
  );

  const lifecycle = sampleEnabled
    ? SAMPLE_LIFECYCLE_BY_RANGE[range]
    : (breakdowns?.lifecycle ?? null);
  const totalRealCount =
    lifecycle === null
      ? 0
      : lifecycle.discovered
        + lifecycle.browsed
        + lifecycle.carted
        + lifecycle.paid
        + lifecycle.fulfilled
        + lifecycle.returned;
  // The empty card renders when sample is off AND we have a settled
  // empty result (loading suppresses the empty flash).
  const showEmpty = !sampleEnabled && !loading && totalRealCount === 0;

  if (showEmpty) {
    return (
      <Card className="mt-6">
        <EmptyStateCard
          icon={Activity}
          label="Stage activity"
          hint="Per-stage agent activity lights up as discovery, payment, and fulfillment events arrive."
        />
      </Card>
    );
  }

  // 2026-06 dashboard cohesion pass — reframed from "funnel" (top-down,
  // each stage a subset of the one above) to STAGE ACTIVITY (independent
  // per-stage counts). The previous funnel framing was misleading: the
  // reporting SQL counts each stage independently, so BROWSED < PAID
  // (a real flow) is normal and breaks the funnel mental model. The
  // CARTED stage is hidden because its predicate has never been wired
  // to a real agent flow — showing a misleading 0 was a defect. The
  // decorative "FULL LIFECYCLE · ACP" pill is removed because it's a
  // static label, not real protocol data.
  const safe = lifecycle ?? { discovered: 0, browsed: 0, carted: 0, paid: 0, fulfilled: 0, returned: 0 };
  const phases: { name: string; count: number; color: string }[] = [
    { name: 'Discovered', count: safe.discovered, color: C.muted },
    { name: 'Browsed', count: safe.browsed, color: C.muted },
    // CARTED intentionally omitted (not tracked yet — would show a
    // misleading 0). Restore once a real predicate lands.
    { name: 'Paid', count: safe.paid, color: C.limeFg },
    { name: 'Fulfilled', count: safe.fulfilled, color: C.limeFg },
    { name: 'Returned', count: safe.returned, color: C.amberFg },
  ];
  return (
    <Card className="px-4 sm:px-6 py-5 mt-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>
            STAGE ACTIVITY · AGENTS · {range.toUpperCase()}
          </div>
          {sampleEnabled && <SampleChip />}
          <div style={{ fontFamily: serifFont, fontSize: '18px', color: C.text, letterSpacing: -0.3, marginTop: 4 }}>
            What agents did <em>at each stage</em>, this period.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mt-4">
        {phases.map((p, i) => (
          <div key={p.name}>
            <div className="flex items-center gap-2 mb-2">
              <div style={{ width: 6, height: 6, background: p.color, borderRadius: 1 }} />
              <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>{p.name.toUpperCase()}</span>
            </div>
            <div style={{ fontFamily: serifFont, fontSize: '22px', color: C.text, lineHeight: 1, letterSpacing: -0.5 }}>{p.count.toLocaleString()}</div>
            <motion.div
              className="mt-2 h-1"
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 0.8, delay: i * 0.08 }}
              style={{ background: p.color, borderRadius: 1, opacity: 0.6 }}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}

// Founder 2026-10-21 — the Overview's "agent visibility" widget now
// mirrors the Agent readiness page byte-for-byte. The previous tile
// read `overview.visibility.score` (a 0-100 weighted sum over the
// agent-card profile fields, via `buildVisibilityReport` on the server)
// and rendered its own "Fully / Strong / Partial / Thin" copy ladder.
// The Agent readiness page's ring reads the full check battery
// (`buildReadinessChecks` + `readinessVerdict` from `@sill/types`) and
// renders a three-state verdict ("Agent-ready" / "Almost ready" / "Not
// ready yet") with mint / amber / coral semantics (CLAUDE.md §4). The
// two surfaces disagreed often enough — a site with a complete agent
// card but no robots.txt / sitemap / policies would read "Fully
// agent-ready" on the Overview and "Almost ready" on the Readiness
// page. We now share `buildReadinessChecks` + `readinessVerdict` as
// the single source + derivation, so both surfaces always agree.
//
// `useReadinessData` is the composite that drives the Agent readiness
// page; mounting it a second time on the Overview reuses the same
// localStorage stale-while-revalidate seed, so the first paint is
// instant for merchants who have visited either page recently.

/** Readiness verdict → ring colour (mint / amber / coral). Mirrors
 *  `verdictArcColor` in `ReadinessRing.tsx`. */
function overviewVerdictColor(kind: ReadinessVerdictKind, C: ReturnType<typeof useC>): string {
  if (kind === 'agent_ready') return C.mintFg;
  if (kind === 'almost_ready') return C.amberFg;
  return C.coralFg;
}

/** Sub-line copy shared with the Readiness page's own "N things to
 *  fix" phrasing. Keeps numbers out of the headline so the mono fraction
 *  (passing/counted) remains the one place a merchant reads the count. */
function overviewVerdictSubline(verdict: ReadinessVerdict): string {
  if (verdict.counted === 0) return 'Open Agent readiness →';
  const total = verdict.counted;
  const open = total - verdict.passing;
  if (open === 0) return 'Nothing to fix · Open Agent readiness →';
  const word = open === 1 ? '1 thing to fix' : `${open.toLocaleString('en-US')} things to fix`;
  return `${word} · Open Agent readiness →`;
}

function VisibilityWidget({
  goTo,
  range = '7d',
}: { goTo: SetView; range?: RangeId }): JSX.Element {
  const C = useC();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const { activeSite } = useSites();

  // No active site: honest empty tile. (The dashboard itself hides most
  // widgets in this state; keep the same shape so parent layout holds.)
  if (activeSite === null) {
    return (
      <Card className="cursor-pointer" onClick={() => goTo('readiness')}>
        <EmptyStateCard icon={Activity} label="Agent readiness" hint="Pick a site to see its readiness." />
      </Card>
    );
  }

  // Sample preview path — fixed verdict so the preview is stable (same
  // discipline as the KPI tiles above). The real data path reads the
  // same hook the Readiness page uses so the two surfaces never drift.
  if (sampleEnabled) {
    void range;
    const sampleVerdict: ReadinessVerdict = { kind: 'almost_ready', label: 'Almost ready', passing: 6, counted: 8 };
    return <ReadinessOverviewCard goTo={goTo} verdict={sampleVerdict} loading={false} has_snapshot={true} sampleChip={true} />;
  }

  // Real-data path — same source + derivation + labels as the Agent
  // readiness page. The composite returns `all_settled` once every
  // primary source has returned at least once; `data.has_snapshot`
  // flips when a probe run has completed.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return <ReadinessOverviewCardLive site={activeSite} goTo={goTo} />;
}

/** Live-data branch extracted so the sample branch above never calls
 *  `useReadinessData`. React hooks must run unconditionally; we keep
 *  the conditional outside this component. */
function ReadinessOverviewCardLive({ site, goTo }: { site: Site; goTo: SetView }): JSX.Element {
  const { data, loading, all_settled, input } = useReadinessData(site);
  const checks = useMemo(() => buildReadinessChecks(input), [input]);
  const verdict = useMemo(() => readinessVerdict(checks), [checks]);

  // Loading = nothing has settled yet AND we have no cached layout. In
  // that state the Readiness page shows "Checking…"; we mirror that.
  const verdict_loading = loading || !all_settled;
  const has_snapshot = data.has_snapshot;

  // When the site has no completed snapshot AND we are not actively
  // checking, show the no-verdict empty tile with a direct CTA — same
  // shape the Readiness header uses.
  if (!has_snapshot && !verdict_loading) {
    return (
      <Card
        className="p-5 cursor-pointer"
        onClick={() => goTo('readiness')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            goTo('readiness');
          }
        }}
        aria-label="Agent readiness — not checked yet. Open Agent readiness."
      >
        <EmptyStateCard icon={Activity} label="Agent readiness" hint="Not checked yet. Open Agent readiness to run your first check." />
      </Card>
    );
  }

  return <ReadinessOverviewCard goTo={goTo} verdict={verdict} loading={verdict_loading} has_snapshot={has_snapshot} sampleChip={false} />;
}

interface ReadinessOverviewCardProps {
  readonly goTo: SetView;
  readonly verdict: ReadinessVerdict;
  readonly loading: boolean;
  readonly has_snapshot: boolean;
  readonly sampleChip: boolean;
}

/** Pure presentational: a ring (counted / passing) + verdict label +
 *  short sub-line. Everything the Readiness page itself renders in its
 *  own header ring. Click opens the Readiness page. */
function ReadinessOverviewCard({ goTo, verdict, loading, has_snapshot, sampleChip }: ReadinessOverviewCardProps): JSX.Element {
  const C = useC();
  const r = 34;
  const circ = 2 * Math.PI * r;
  const pct = verdict.counted === 0 ? 0 : verdict.passing / verdict.counted;
  const arcColor = overviewVerdictColor(verdict.kind, C);

  const headline = loading
    ? 'Checking…'
    : verdict.label;
  const subline = loading
    ? 'Open Agent readiness →'
    : overviewVerdictSubline(verdict);
  const fraction = loading || verdict.counted === 0 || !has_snapshot
    ? '—'
    : `${verdict.passing}/${verdict.counted}`;

  const aria = loading
    ? 'Agent readiness — checking. Open Agent readiness.'
    : `Agent readiness — ${verdict.label}, ${verdict.passing} of ${verdict.counted} checks passing. Open Agent readiness.`;

  return (
    <Card
      className="p-5 cursor-pointer"
      onClick={() => goTo('readiness')}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goTo('readiness');
        }
      }}
      aria-label={aria}
    >
      <div className="flex items-center justify-between mb-1">
        <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>AGENT READINESS</span>
        {sampleChip && <SampleChip />}
        <ArrowUpRight size={12} color={C.muted} />
      </div>
      <div className="flex items-center gap-4 mt-3">
        <div className="relative flex items-center justify-center" style={{ width: 80, height: 80 }}>
          <svg width="80" height="80" viewBox="0 0 80 80" aria-hidden="true">
            <circle cx="40" cy="40" r={r} stroke={C.surface2} strokeWidth="6" fill="none" />
            {!loading && (
              <motion.circle
                cx="40"
                cy="40"
                r={r}
                fill="none"
                stroke={arcColor}
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={circ}
                initial={{ strokeDashoffset: circ }}
                animate={{ strokeDashoffset: circ * (1 - pct) }}
                transition={{ duration: 1.4, ease: 'easeOut' }}
                transform="rotate(-90 40 40)"
              />
            )}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span style={{ fontFamily: monoFont, fontSize: 14, color: C.text, letterSpacing: 0.5 }}>{fraction}</span>
          </div>
        </div>
        <div className="flex-1">
          <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, fontWeight: 500, lineHeight: 1.3 }}>{headline}</div>
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>{subline}</div>
        </div>
      </div>
    </Card>
  );
}

interface TrafficPoint {
  day: string;
  human: number;
  agent: number;
}

// ADR-0115 Slice B — sample traffic preview. Kept stable across renders
// so the "what the chart will look like" preview is predictable.
const SAMPLE_TRAFFIC_DATA: TrafficPoint[] = [
  { day: 'Apr 30', human: 412, agent: 184 },
  { day: 'May 01', human: 387, agent: 246 },
  { day: 'May 02', human: 401, agent: 312 },
  { day: 'May 03', human: 298, agent: 478 },
  { day: 'May 04', human: 312, agent: 612 },
  { day: 'May 05', human: 389, agent: 821 },
  { day: 'May 06', human: 432, agent: 1124 },
];

// ADR-0115 Slice B — short label for a bucket boundary. Hour for the
// 24h range ("14:00"); short date for everything else ("Jun 08"). UTC
// throughout so a merchant in any locale sees the same bucket label
// the server-side `evaluated_at AT TIME ZONE 'UTC'` produced. The
// helper does NOT depend on the dashboard's locale (i18n note,
// ADR-0115 hard constraint: no English-only assumptions in new server
// logic; the locale here is hard-coded to en-US to stay deterministic
// when the dashboard ships in multiple locales, NOT to assume the
// merchant reads English).
export function formatTrafficBucketLabel(iso: string, granularity: 'hour' | 'day' | 'week'): string {
  const d = new Date(iso);
  if (granularity === 'hour') {
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
  }
  // 'day' and 'week' both render a date label (week = the bucket's start date).
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', timeZone: 'UTC' });
}

/**
 * ADR-0115 Slice B (must-fix) — compute the inline-SVG primitives a
 * traffic area chart needs. Defended against three real-data edge
 * cases the sample-only renderer ignored:
 *
 *   - **Empty series** — `Math.max(...[])` returns `-Infinity` →
 *     `yAt` would yield `NaN` SVG paths. We clamp `maxY` to a
 *     minimum of 1 so a fresh site with no traffic still renders a
 *     flat baseline instead of an unrendered `<path d="NaN…">`.
 *   - **All-zero series** — same clamp covers `maxY === 0`.
 *   - **Single-point series** — `xAt = (i/(data.length-1))*W` divides
 *     by zero when `data.length === 1`. We render a flat baseline at
 *     `y = H` (the bottom edge) for a single point so the bucket
 *     appears as a tiny visible spike at the chart's center.
 *
 * Pure function, exported for tests. Returns 0 for the y of a
 * single-point series (full-baseline; the chart degenerates safely).
 */
export interface TrafficGeometry {
  width: number;
  height: number;
  maxY: number;
  xAt: (i: number) => number;
  yAt: (v: number) => number;
  lineFor: (key: 'human' | 'agent', data: TrafficPoint[]) => string;
  areaFor: (key: 'human' | 'agent', data: TrafficPoint[]) => string;
}

export function buildTrafficGeometry(data: TrafficPoint[]): TrafficGeometry {
  const W = 600;
  const H = 200;
  // `Math.max(1, …)` guards both empty (-Infinity) and all-zero (0)
  // series. With `maxY === 1`, `yAt(0) === H` (baseline) and
  // `yAt(1) === 0` (top); a fresh series renders as a flat baseline.
  const counts = data.flatMap((d) => [d.human, d.agent]);
  const maxY = Math.max(1, ...counts);
  // `data.length - 1` is 0 for a single-point series — center the
  // point at x=W/2 (a tiny visible spike) instead of NaN-dividing.
  const xAt = (i: number): number => {
    if (data.length <= 1) {
      return W / 2;
    }
    return (i / (data.length - 1)) * W;
  };
  const yAt = (v: number): number => H - (v / maxY) * H;
  const lineFor = (key: 'human' | 'agent', d: TrafficPoint[]): string => {
    if (d.length === 0) {
      return `M 0 ${H} L ${W} ${H}`;
    }
    if (d.length === 1) {
      const p = d[0] as TrafficPoint;
      const y = yAt(p[key]);
      return `M 0 ${y.toFixed(1)} L ${W} ${y.toFixed(1)}`;
    }
    return d.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(p[key]).toFixed(1)}`).join(' ');
  };
  const areaFor = (key: 'human' | 'agent', d: TrafficPoint[]): string => `${lineFor(key, d)} L ${W} ${H} L 0 ${H} Z`;
  return { width: W, height: H, maxY, xAt, yAt, lineFor, areaFor };
}

// 2026-06 dashboard cohesion pass — thin the x-axis labels for wide
// ranges. At 90d the raw data has 90 buckets; rendering 90 cramped
// labels is unreadable. We pick `target` evenly-spaced ticks and emit
// a sparse array (empty strings render as invisible spacers so the
// flex justify-between geometry doesn't shift).
//
// `target` is the maximum number of visible tick labels — 7 by default,
// chosen so 7d → 7 labels (unchanged), 30d → ~7 sampled, 90d → ~7
// sampled. Returns the SAME-length array as input so the flex layout
// stays one-per-bucket; empty strings render as invisible spacers.
export function thinAxisLabels(labels: string[], target = 7): string[] {
  const n = labels.length;
  if (n <= target) return labels.slice();
  // Use a stride so first + last are always shown, and the rest are
  // sampled evenly. `Math.ceil` keeps the visible count at-or-under
  // `target`.
  const stride = Math.ceil((n - 1) / (target - 1));
  const out = labels.map(() => '');
  for (let i = 0; i < n; i += stride) {
    out[i] = labels[i] as string;
  }
  // Always pin the last label even if the stride skipped it.
  out[n - 1] = labels[n - 1] as string;
  return out;
}

function TrafficChart({ range = '7d' }: { range?: RangeId }): JSX.Element {
  const C = useC();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const { activeSite } = useSites();
  // ADR-0115 Slice B — real series replaces the hard-coded preview
  // when sample is off. Fetch is skipped on sample-on (no wasted call).
  const { traffic, loading } = useReportingTraffic(
    activeSite?.site_id as SiteId | undefined,
    range,
    !sampleEnabled,
  );

  // Hover state — index of the nearest point under the cursor, plus
  // raw SVG x for the cursor line. `null` = no hover; tooltip is
  // suppressed. Driven by pointer-move on the chart's hit overlay.
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const data: TrafficPoint[] = sampleEnabled
    ? SAMPLE_TRAFFIC_DATA
    : (traffic?.points ?? []).map((p) => ({
        day: formatTrafficBucketLabel(p.bucket_start, traffic?.granularity ?? 'day'),
        human: p.human,
        agent: p.agent,
      }));

  // Empty card when sample is off, the fetch settled, and the series
  // is all-zero (a true fresh site). A loading state stays muted on
  // the chart — no flash of the empty card while the fetch is in
  // flight.
  const totalTraffic = data.reduce((s, p) => s + p.human + p.agent, 0);
  const showEmpty = !sampleEnabled && !loading && totalTraffic === 0;

  if (showEmpty) {
    return (
      <Card>
        <EmptyStateCard icon={Activity} label="Traffic · human vs agent" hint="Traffic timelines appear once agents start calling your endpoint." />
      </Card>
    );
  }

  // Inline SVG area chart (no recharts dependency — CLAUDE.md §8).
  // The must-fix data-lens guards live in `buildTrafficGeometry`.
  const { width: W, height: H, xAt, yAt, lineFor, areaFor } = buildTrafficGeometry(data);
  const axisLabels = thinAxisLabels(data.map((d) => d.day));

  // Nearest-point pickup. `e.currentTarget` is the hit overlay; we map
  // its bounding-box-relative cursor x back into the SVG's viewBox
  // coordinate space so xAt(i) comparisons are honest.
  const handleMove = (e: React.PointerEvent<SVGRectElement>): void => {
    if (data.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width === 0) return;
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    // Walk the points; cheapest correct option for sub-100-bucket
    // series. The single-point geometry collapses xAt(0) === W/2; the
    // pickup still works.
    let best = 0;
    let bestDist = Math.abs(xAt(0) - svgX);
    for (let i = 1; i < data.length; i += 1) {
      const d = Math.abs(xAt(i) - svgX);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    setHoverIdx(best);
  };
  const handleLeave = (): void => setHoverIdx(null);
  // Tooltip x-position in PERCENT of the SVG width (`preserveAspectRatio="none"`
  // means the SVG stretches to the container; percentages keep the
  // tooltip anchored to the actual data point even when the container
  // resizes).
  const hovered: TrafficPoint | null =
    hoverIdx !== null && hoverIdx >= 0 && hoverIdx < data.length
      ? (data[hoverIdx] as TrafficPoint)
      : null;
  const hoverPct = hoverIdx !== null ? (xAt(hoverIdx) / W) * 100 : 0;

  const totalAgentRequests = data.reduce((s, p) => s + p.agent, 0);

  return (
    <Card className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <div>
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 4 }}>TRAFFIC · HUMAN VS AGENT</div>
          {sampleEnabled && <SampleChip />}
          <div className="flex items-baseline gap-3 flex-wrap">
            <div style={{ fontFamily: serifFont, fontSize: '20px', color: C.text, letterSpacing: -0.5 }}>
              {sampleEnabled
                ? <>Agent traffic crossed human last <em>Saturday</em>.</>
                : <>Human <em>vs</em> agent visits, by bucket.</>}
            </div>
            {!sampleEnabled && totalAgentRequests > 0 && (
              <div className="flex items-baseline gap-1.5">
                <span style={{ fontFamily: serifFont, fontSize: '22px', color: C.limeFg, letterSpacing: -0.5 }}>{totalAgentRequests.toLocaleString()}</span>
                <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>AI REQUESTS</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div style={{ width: 10, height: 2, background: C.muted }} />
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>HUMAN</span>
          </div>
          <div className="flex items-center gap-2">
            <div style={{ width: 10, height: 2, background: C.limeFg }} />
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>AGENT</span>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 24, position: 'relative' }}>
        <svg width="100%" height={220} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Human versus agent traffic over time">
          <defs>
            <linearGradient id="agentGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={C.limeFg} stopOpacity={0.5} />
              <stop offset="100%" stopColor={C.limeFg} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="humanGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={C.muted} stopOpacity={0.25} />
              <stop offset="100%" stopColor={C.muted} stopOpacity={0} />
            </linearGradient>
          </defs>
          <path d={areaFor('human', data)} fill="url(#humanGrad)" stroke="none" />
          <path d={areaFor('agent', data)} fill="url(#agentGrad)" stroke="none" />
          <path d={lineFor('human', data)} fill="none" stroke={C.muted} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
          <path d={lineFor('agent', data)} fill="none" stroke={C.limeFg} strokeWidth={2} vectorEffect="non-scaling-stroke" />
          {/* Hover cursor line + value markers — only visible when
              hoverIdx is set. Drawn ABOVE the data paths but BELOW the
              hit overlay so the pointer keeps picking up moves. */}
          {hovered !== null && hoverIdx !== null && (
            <g>
              <line
                x1={xAt(hoverIdx)}
                x2={xAt(hoverIdx)}
                y1={0}
                y2={H}
                stroke={C.muted}
                strokeWidth={1}
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
                opacity={0.7}
              />
              <circle cx={xAt(hoverIdx)} cy={yAt(hovered.human)} r={3} fill={C.muted} vectorEffect="non-scaling-stroke" />
              <circle cx={xAt(hoverIdx)} cy={yAt(hovered.agent)} r={3} fill={C.limeFg} vectorEffect="non-scaling-stroke" />
            </g>
          )}
          {/* Invisible hit overlay — captures pointer moves across the
              full chart area. Drawing it inside the SVG (rather than a
              sibling DOM div) keeps the cursor x calculation in viewBox
              units, and means the overlay tracks the SVG's own resize. */}
          <rect
            x={0}
            y={0}
            width={W}
            height={H}
            fill="transparent"
            onPointerMove={handleMove}
            onPointerLeave={handleLeave}
            style={{ cursor: 'crosshair' }}
            data-testid="traffic-chart-hit-overlay"
          />
        </svg>
        {/* Tooltip — absolute-positioned over the chart area. Renders
            outside the SVG so it can use HTML text + the standard
            Tooltip-style chrome. Suppressed when nothing is hovered. */}
        {hovered !== null && (
          <div
            role="tooltip"
            data-testid="traffic-chart-tooltip"
            className="pointer-events-none absolute"
            style={{
              left: `${hoverPct}%`,
              top: 0,
              transform: 'translate(-50%, -100%)',
              background: C.surface3,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              padding: '6px 10px',
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
              whiteSpace: 'nowrap',
              minWidth: 120,
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 4 }}>
              {hovered.day.toUpperCase()}
            </div>
            <div className="flex items-center justify-between gap-3">
              <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>HUMAN</span>
              <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>{hovered.human.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span style={{ fontFamily: monoFont, fontSize: 10, color: C.limeFg }}>AGENT</span>
              <span style={{ fontFamily: monoFont, fontSize: 12, color: C.limeFg }}>{hovered.agent.toLocaleString()}</span>
            </div>
          </div>
        )}
        <div className="flex justify-between mt-2">
          {axisLabels.map((label, i) => (
            <span
              key={`${label || 'gap'}-${i}`}
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.dim,
                // Empty labels keep the flex-justify-between geometry
                // unchanged but render as invisible — no layout shift.
                visibility: label === '' ? 'hidden' : 'visible',
                minWidth: 1,
              }}
            >
              {label || '·'}
            </span>
          ))}
        </div>
      </div>
    </Card>
  );
}

interface AgentRevenue {
  agent: string;
  /** Raw registry slug (e.g. `agent_openai_searchbot`) — kept so the
   *  deep-link to Mandates passes the slug instead of the brand name,
   *  which often doesn't substring-match the stored agent_id. Absent on
   *  sample data rows (sample rows deep-link via display name). */
  agent_id?: string | null;
  /** `unknown_agent` or `human_likely` — used as the filter value when
   *  agent_id is null. Absent on sample data rows. */
  visitor_class?: string;
  revenue: number;
  calls: number;
  color: string;
  currency: string | null;
}

// ADR-0115 Slice C / 2026-06-18 founder reframe — the widget is now a
// CALLS chart (volume), with revenue alongside as a secondary metric.
// One $0-revenue row is intentional so the preview demos the legitimate
// "lots of calls, no sales yet" case that Phase-1 discovery-only sites
// hit by default.
const SAMPLE_REVENUE_BY_AGENT: AgentRevenue[] = [
  { agent: 'ChatGPT', revenue: 389200, calls: 1847, color: '#10A37F', currency: 'USD' },
  { agent: 'Perplexity', revenue: 210400, calls: 1283, color: '#20B8CD', currency: 'USD' },
  { agent: 'Claude', revenue: 154700, calls: 894, color: '#CC785C', currency: 'USD' },
  { agent: 'Gemini', revenue: 50400, calls: 612, color: '#4285F4', currency: 'USD' },
  { agent: 'Bingbot', revenue: 0, calls: 412, color: '#0078D4', currency: 'USD' },
  { agent: 'Custom', revenue: 20000, calls: 256, color: '#888', currency: 'USD' },
];

// ADR-0115 Slice C — render-color fallback for real rows. The sample
// path keeps its brand-specific palette; real rows tone by visitor
// class so the bar still carries a glance-readable signal.
//
// 2026-06-19 — non-matched rows used to take `C.muted` (grey), which
// rendered as an effectively-invisible dot on the bar's leading edge
// — the row read as "no icon" next to the sample's colored dots. Use
// `C.sky` (the pending blue, #7FB8FF) for unidentified/human/unknown
// rows so the dot is visibly present while still tonally distinct from
// the lime "matched agent" affirmation. CLAUDE.md §4 — no new tokens;
// `sky` is the existing semantic-pending color.
function agentBarColor(
  visitor_class: ReportingBreakdowns['by_agent'][number]['visitor_class'],
  C: ReturnType<typeof useC>,
): string {
  if (visitor_class === 'matched_agent') return C.limeFg;
  return C.sky;
}

function RevenueByAgent({
  goTo,
  range = '7d',
}: { goTo: SetView; range?: RangeId }): JSX.Element {
  const C = useC();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const { activeSite } = useSites();
  const { breakdowns, loading } = useReportingBreakdowns(
    activeSite?.site_id as SiteId | undefined,
    range,
    !sampleEnabled,
  );

  // Founder reframe (2026-06-18) — the widget is a CALLS chart
  // (activity volume), not a SPEND chart. Discovery-only agents legitimately
  // ride with $0 revenue, and that's worth seeing alongside the heavy
  // hitters; the breakdown row already carries `calls` + `revenue_minor`
  // (server-ordered calls DESC), so we drop the previous revenue > 0
  // filter and let every row through.
  //
  // Brand-clean labels: the breakdown wire carries the raw slug, so we
  // route registered slugs through `shortAgentDisplayName` (ChatGPT /
  // Claude / Gemini) instead of the long "Org · Product" form that read
  // as a registry dump on a row card.
  // 2026-06-19 — the server-side `by_agent` breakdown groups by
  // `(agent_id, visitor_class)`. A site with NULL `agent_id` rows
  // across two different visitor_class values (e.g. `unknown_agent`
  // + `human_likely` both mapped to "Unidentified agent" / "Human
  // visitor") emits multiple rows that collapse to the same display
  // label, producing duplicate bars on the chart. Merge after the
  // label projection so the chart shows one bar per displayed label.
  // The first row's `color`/`currency` win on tie (the server returns
  // calls-DESC, so we re-sort by calls desc after merging to preserve
  // the existing ordering).
  const data: AgentRevenue[] = sampleEnabled
    ? SAMPLE_REVENUE_BY_AGENT
    : (() => {
        const projected: AgentRevenue[] = (breakdowns?.by_agent ?? []).map((row) => ({
          agent:
            row.agent_id !== null
              ? shortAgentDisplayName(row.agent_id)
              : row.visitor_class === 'human_likely'
                ? 'Human visitor'
                : 'Unidentified agent',
          agent_id: row.agent_id,
          visitor_class: row.visitor_class,
          revenue: row.revenue_minor,
          calls: row.calls,
          color: agentBarColor(row.visitor_class, C),
          currency: row.currency,
        }));
        const byLabel = new Map<string, AgentRevenue>();
        for (const row of projected) {
          const existing = byLabel.get(row.agent);
          if (existing === undefined) {
            byLabel.set(row.agent, { ...row });
          } else {
            existing.calls += row.calls;
            existing.revenue += row.revenue;
          }
        }
        return Array.from(byLabel.values()).sort((a, b) => b.calls - a.calls);
      })();

  // Bars are ranked by CALLS (volume), not revenue. The server already
  // returns the breakdown calls-DESC; we keep that order on the row
  // map. The `Math.max(maxCalls, 1)` guard handles the (rare) all-zero
  // series so the width is `0%` instead of NaN.
  const maxCalls = Math.max(1, ...data.map((d) => d.calls));

  // Empty-card gate: sample-off, fetch settled, NO rows at all (the
  // breakdown was empty). Previously this gated on "no rows with
  // revenue" — a discovery-only site landed here even though the
  // breakdown carried real calls.
  const showEmpty = !sampleEnabled && !loading && data.length === 0;
  if (showEmpty) {
    return (
      <Card className="mt-6">
        <EmptyStateCard
          icon={DollarSign}
          label="No calls from agents in this period"
          hint="Agent calls + revenue break down per-caller once your endpoint sees traffic."
        />
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6 mt-6">
      <div className="mb-6">
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 4 }}>AGENTS BY CALLS</div>
        {sampleEnabled && <SampleChip />}
        <div style={{ fontFamily: serifFont, fontSize: '18px', color: C.text, letterSpacing: -0.5 }}>
          {sampleEnabled
            ? <>ChatGPT is doing the most <em>talking</em>.</>
            : <>Calling agents <em>ranked</em> by call volume.</>}
        </div>
      </div>
      <div className="space-y-3">
        {data.map((d) => {
          // Bars are sized by CALLS (volume), not revenue — a
          // discovery-only agent with lots of calls is now a load-bearing
          // row, not a filtered-out edge case.
          const pct = (d.calls / maxCalls) * 100;
          // "—" rows are discovery-only callers (read-only skill access;
          // no signed mandate yet). The bar reflects their call activity;
          // the revenue cell renders an em-dash so the column doesn't
          // assert a zero where the row is genuinely a non-payment caller.
          const discoveryOnly = d.revenue === 0;
          // Founder feedback (2026-06) — per-agent deep-link. The
          // Mandates view's search box matches against agent display
          // names (e.g. "ChatGPT"), so a `q=<agent_name>` deep-link
          // pre-filters the list to that agent's mandates. The
          // RevenueByAgent row is a per-agent aggregate with no
          // single record to open, so this is the right granularity.
          const tooltipText = discoveryOnly
            ? 'Discovery-only — this agent has called read-only skills but has not signed a paying mandate yet.'
            : `View all mandates from ${d.agent} in the mandates surface.`;
          const goRow = (): void => {
            // Pass the raw registry slug (e.g. `agent_openai_searchbot`) so
            // the Mandates search matches the stored agent_id field. The brand
            // display name ("ChatGPT Search") often doesn't substring-match the
            // slug or UA. For unidentified/human rows where agent_id is null,
            // fall back to visitor_class ("unknown_agent" / "human_likely") which
            // is included in the server's ILIKE filter. Sample data rows lack
            // both fields — fall back to the display name in that case.
            const filter_q = d.agent_id !== undefined
              ? (d.agent_id ?? d.visitor_class ?? d.agent)
              : d.agent;
            // People are not under the visits panel's default "All" chip:
            // open the People chip instead of searching "human_likely".
            goTo('mandates', d.agent_id == null && d.visitor_class === 'human_likely' ? { segment: 'people' } : { q: filter_q });
          };
          return (
            <Tooltip key={d.agent} content={tooltipText} side="top" displayMode="block">
              <div
                className="grid grid-cols-12 items-center gap-2 sm:gap-4 cursor-pointer"
                onClick={goRow}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goRow();
                  }
                }}
                aria-label={`Revenue from ${d.agent} — ${d.calls.toLocaleString()} calls — open mandates`}
                data-testid="revenue-by-agent-row"
                style={{ padding: '2px 0' }}
              >
                <div className="col-span-3 sm:col-span-2 flex items-center gap-2 min-w-0">
                  <div style={{ width: 6, height: 6, borderRadius: 1, background: d.color, flexShrink: 0 }} />
                  <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.agent}</span>
                </div>
                <div className="col-span-6 sm:col-span-7">
                  <div style={{ height: 6, background: C.surface2, borderRadius: 1, overflow: 'hidden' }}>
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1, ease: 'easeOut' }} style={{ height: '100%', background: d.color }} />
                  </div>
                </div>
                <div className="hidden sm:block col-span-2" style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, textAlign: 'right' }}>{d.calls.toLocaleString()}</div>
                <div className="col-span-3 sm:col-span-1" style={{ fontFamily: monoFont, fontSize: 12, color: discoveryOnly ? C.muted : C.text, textAlign: 'right' }}>
                  {/* Discovery-only callers have revenue = 0; render an em-dash so the bar
                      is meaningful but the dollar column doesn't lie. The tooltip above
                      clarifies why the row is showing zero revenue. */}
                  {discoveryOnly
                    ? '—'
                    : d.currency !== null
                      ? formatMinor(d.revenue, d.currency)
                      : formatMinor(d.revenue)}
                </div>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AIOperatorsCard — groups `by_agent` by company prefix client-side.
// Shows each AI operator (OpenAI, Anthropic, Google…) with total call
// count and a proportional bar. No new fetch — reuses the breakdowns hook.
// ---------------------------------------------------------------------------

const OPERATOR_LABEL: Record<string, string> = {
  agent_openai_: 'OpenAI',
  agent_anthropic_: 'Anthropic',
  agent_google_: 'Google',
  agent_microsoft_: 'Microsoft',
  agent_perplexity: 'Perplexity',
  agent_meta_: 'Meta',
  agent_apple_: 'Apple',
  agent_duckduckgo_: 'DuckDuckGo',
  agent_mistral_: 'Mistral',
  agent_x_: 'X',
  agent_linkedin_: 'LinkedIn',
  agent_slack_: 'Slack',
  agent_discord_: 'Discord',
};

function operatorForAgentId(agent_id: string | null): string {
  if (agent_id === null) return 'Other';
  for (const [prefix, label] of Object.entries(OPERATOR_LABEL)) {
    if (agent_id.startsWith(prefix)) return label;
  }
  return 'Other';
}

const SAMPLE_OPERATORS = [
  { operator: 'OpenAI', calls: 3074, color: '#10A37F' },
  { operator: 'Anthropic', calls: 894, color: '#CC785C' },
  { operator: 'Google', calls: 612, color: '#4285F4' },
  { operator: 'Microsoft', calls: 412, color: '#0078D4' },
  { operator: 'Perplexity', calls: 1283, color: '#20B8CD' },
];

const SAMPLE_PAGES: ReadonlyArray<{ page_path: string; count: number }> = [
  { page_path: '/products', count: 1240 },
  { page_path: '/collections/mens', count: 892 },
  { page_path: '/collections/womens', count: 741 },
  { page_path: '/', count: 634 },
  { page_path: '/pages/about', count: 218 },
];

const SAMPLE_SEARCH_TERMS: ReadonlyArray<{ term: string; count: number }> = [
  { term: 'running shoes', count: 412 },
  { term: 'nike air max', count: 287 },
  { term: 'sale', count: 203 },
  { term: 'mens sneakers', count: 176 },
  { term: 'womens boots', count: 134 },
];

function AIOperatorsCard({ range = '7d' }: { range?: RangeId }): JSX.Element {
  const C = useC();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const { activeSite } = useSites();
  const { breakdowns, loading } = useReportingBreakdowns(
    activeSite?.site_id as SiteId | undefined,
    range,
    !sampleEnabled,
  );

  const operators: { operator: string; calls: number; color: string }[] = sampleEnabled
    ? SAMPLE_OPERATORS
    : (() => {
        const tally = new Map<string, number>();
        for (const row of breakdowns?.by_agent ?? []) {
          if (row.visitor_class !== 'matched_agent') continue;
          const op = operatorForAgentId(row.agent_id);
          tally.set(op, (tally.get(op) ?? 0) + row.calls);
        }
        return Array.from(tally.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([operator, calls]) => ({ operator, calls, color: C.limeFg }));
      })();

  const totalCalls = operators.reduce((s, o) => s + o.calls, 0);
  const maxCalls = Math.max(1, ...operators.map((o) => o.calls));

  const showEmpty = !sampleEnabled && !loading && operators.length === 0;
  if (showEmpty) {
    return (
      <Card>
        <EmptyStateCard
          icon={Bot}
          label="AI operators"
          hint="Operator breakdown appears once identified agents call your endpoint."
        />
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6">
      <div className="mb-5">
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 4 }}>AI OPERATORS</div>
        {sampleEnabled && <SampleChip />}
        <div style={{ fontFamily: serifFont, fontSize: '18px', color: C.text, letterSpacing: -0.5 }}>
          {sampleEnabled
            ? <>OpenAI is driving most of your <em>agent traffic</em>.</>
            : <>{operators[0]?.operator ?? 'Unknown'} leads identified <em>operator traffic</em>.</>}
        </div>
      </div>
      <div className="flex items-center gap-5 flex-wrap">
        <Donut
          slices={operators.map((o) => ({ label: o.operator, value: o.calls, color: o.color }))}
          size={132}
          thickness={14}
          centerLabel="CALLS"
          formatValue={(n) => n.toLocaleString()}
          aria-label="Share of identified operator traffic"
        />
        <div className="flex-1 min-w-[160px] space-y-2">
          {operators.map((o) => {
            const sharePct = totalCalls > 0 ? Math.round((o.calls / totalCalls) * 100) : 0;
            return (
              <div key={o.operator} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span style={{ width: 9, height: 9, borderRadius: 1, background: o.color, flexShrink: 0, opacity: sampleEnabled ? 0.7 : 1 }} />
                  <span style={{ fontFamily: sansFont, fontSize: 12.5, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.operator}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>{sharePct}%</span>
                  <span style={{ fontFamily: monoFont, fontSize: 11, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{o.calls.toLocaleString()}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// OutcomeBar — segmented bar of policy outcomes (observed / approved /
// escalated / blocked). Groups the `by_decision` rollup into four buckets.
// ---------------------------------------------------------------------------

// Decision → display bucket mapping. The bucket ordering drives the bar
// segment order: observed first (most common on Phase-1 sites), then
// approved, escalated, blocked.
type OutcomeBucket = 'observed' | 'approved' | 'escalated' | 'blocked';

function bucketForDecision(decision: string): OutcomeBucket {
  if (decision === 'observed') return 'observed';
  if (decision === 'approved') return 'approved';
  if (decision === 'escalated' || decision === 'escalated_rejected') return 'escalated';
  return 'blocked';
}

const OUTCOME_BUCKET_LABEL: Record<OutcomeBucket, string> = {
  observed: 'Observed',
  approved: 'Approved',
  escalated: 'Escalated',
  blocked: 'Blocked',
};

function OutcomeBar({ range = '7d' }: { range?: RangeId }): JSX.Element {
  const C = useC();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const { activeSite } = useSites();
  const { breakdowns, loading } = useReportingBreakdowns(
    activeSite?.site_id as SiteId | undefined,
    range,
    !sampleEnabled,
  );

  // Bucket colors — semantic palette (CLAUDE.md §4).
  const bucketColor: Record<OutcomeBucket, string> = {
    observed: C.muted,
    approved: C.limeFg,
    escalated: C.amberFg,
    blocked: C.coralFg,
  };

  const SAMPLE_BUCKETS: Record<OutcomeBucket, number> = {
    observed: 4012,
    approved: 487,
    escalated: 21,
    blocked: 42,
  };

  const buckets: Record<OutcomeBucket, number> = sampleEnabled
    ? SAMPLE_BUCKETS
    : (() => {
        const t: Record<OutcomeBucket, number> = { observed: 0, approved: 0, escalated: 0, blocked: 0 };
        for (const row of breakdowns?.by_decision ?? []) {
          t[bucketForDecision(row.decision)] += row.count;
        }
        return t;
      })();

  const total = (Object.values(buckets) as number[]).reduce((s, n) => s + n, 0);

  const showEmpty = !sampleEnabled && !loading && total === 0;
  if (showEmpty) {
    return (
      <Card>
        <EmptyStateCard
          icon={ShieldAlert}
          label="Policy outcomes"
          hint="Outcome breakdown appears once agents call your endpoint."
        />
      </Card>
    );
  }

  const BUCKETS: OutcomeBucket[] = ['observed', 'approved', 'escalated', 'blocked'];

  return (
    <Card className="p-4 sm:p-6">
      <div className="mb-5">
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 4 }}>POLICY OUTCOMES</div>
        {sampleEnabled && <SampleChip />}
        <div style={{ fontFamily: serifFont, fontSize: '18px', color: C.text, letterSpacing: -0.5 }}>
          {sampleEnabled
            ? <>Most traffic is <em>observed</em> with no policy friction.</>
            : <>{total.toLocaleString()} total decisions this <em>period</em>.</>}
        </div>
      </div>
      {/* Segmented bar — hover a segment for its share */}
      <div className="mb-4">
        <SegmentBar
          slices={BUCKETS.map((b) => ({ label: OUTCOME_BUCKET_LABEL[b], value: buckets[b], color: bucketColor[b] }))}
          height={12}
          formatValue={(n) => n.toLocaleString()}
        />
      </div>
      {/* Legend rows */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 mt-1">
        {BUCKETS.map((b) => {
          const n = buckets[b];
          const pct = total > 0 ? Math.round((n / total) * 100) : 0;
          return (
            <div key={b} className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div style={{ width: 8, height: 8, borderRadius: 1, background: bucketColor[b], flexShrink: 0 }} />
                <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>{OUTCOME_BUCKET_LABEL[b]}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>{pct}%</span>
                <span style={{ fontFamily: monoFont, fontSize: 11, color: n > 0 ? C.text : C.muted }}>{n.toLocaleString()}</span>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ProtocolMixCard — "How agents reach your site" (ADR-0253 §A).
// Counts by derived inbound protocol over the selected range. Reads
// `by_protocol` from the breakdowns rollup — no extra round trip. Clicking
// a row filters Activity by that protocol (follow-up: wire the filter chip
// in Mandates.tsx; today the row link opens Activity with the base URL).
// ---------------------------------------------------------------------------

/**
 * ADR-0253 §A — exported so the Agents → Overview card reuses the exact
 * same component (data + layout) via `useReportingBreakdowns`.
 */
export function ProtocolMixCard({
  range = '7d',
  goTo,
}: {
  range?: RangeId;
  goTo: SetView;
}): JSX.Element {
  const C = useC();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const { activeSite } = useSites();
  const { breakdowns, loading } = useReportingBreakdowns(
    activeSite?.site_id as SiteId | undefined,
    range,
    !sampleEnabled,
  );

  const rows = (breakdowns?.by_protocol ?? []).filter((r) => r.visits > 0);
  const totalVisits = rows.reduce((acc, r) => acc + r.visits, 0);

  // Order: show in the vocabulary's natural order (common → rare) so the
  // card reads consistently across sites. `unknown` sinks to the end.
  const ORDER: readonly string[] = ['web', 'mcp', 'a2a', 'ap2', 'ucp', 'acp', 'pap', 'unknown'];
  const sorted = [...rows].sort((a, b) => {
    const ia = ORDER.indexOf(a.protocol);
    const ib = ORDER.indexOf(b.protocol);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });

  const showEmpty = !sampleEnabled && !loading && sorted.length === 0;
  if (showEmpty) {
    return (
      <Card>
        <EmptyStateCard
          icon={Activity}
          label="How agents reach you"
          hint="A per-protocol breakdown appears once agents start calling your site."
        />
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6">
      <div className="mb-5">
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 4 }}>
          HOW AGENTS REACH YOU
        </div>
        <div style={{ fontFamily: serifFont, fontSize: '18px', color: C.text, letterSpacing: -0.5 }}>
          Visits by <em>protocol</em>.
        </div>
      </div>
      <div className="space-y-3">
        {sorted.map((row) => {
          const pct = totalVisits > 0 ? (row.visits / totalVisits) * 100 : 0;
          const label = INBOUND_PROTOCOL_LABEL[row.protocol as keyof typeof INBOUND_PROTOCOL_LABEL]
            ?? row.protocol.toUpperCase();
          return (
            <button
              type="button"
              key={row.protocol}
              // ADR-0253 §A — deep-link into Activity with the protocol
              // chip pre-selected (Mandates reads `?protocol=` on mount).
              onClick={() => {
                if (typeof window !== 'undefined') {
                  const url = new URL(window.location.href);
                  url.searchParams.set('protocol', row.protocol);
                  window.history.replaceState(window.history.state, '', url.toString());
                }
                goTo('mandates');
              }}
              className="w-full text-left"
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  style={{ fontFamily: monoFont, fontSize: 11, color: C.text, letterSpacing: 1 }}
                >
                  {label}
                </span>
                <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                  {row.visits.toLocaleString()}
                  {totalVisits > 0 && <> · {pct.toFixed(pct >= 10 ? 0 : 1)}%</>}
                </span>
              </div>
              <div style={{ height: 4, background: C.surface2, borderRadius: 1, overflow: 'hidden', marginTop: 4 }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                  style={{ height: '100%', background: C.limeFg ?? C.text }}
                />
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// TopPagesCard — pages most visited by AI crawlers. Reads `by_page` from
// the breakdowns rollup (ADR-0203). Shows top 10 paths, horizontal bar.
// ---------------------------------------------------------------------------

function TopPagesCard({ range = '7d' }: { range?: RangeId }): JSX.Element {
  const C = useC();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const { activeSite } = useSites();
  const { breakdowns, loading } = useReportingBreakdowns(
    activeSite?.site_id as SiteId | undefined,
    range,
    !sampleEnabled,
  );

  const pages = sampleEnabled
    ? SAMPLE_PAGES
    : (breakdowns?.by_page ?? []).slice(0, 10);

  const maxCount = Math.max(1, ...pages.map((p) => p.count));

  const showEmpty = !sampleEnabled && !loading && pages.length === 0;
  if (showEmpty) {
    return (
      <Card>
        <EmptyStateCard
          icon={Globe}
          label="Top pages"
          hint="Page-level breakdown appears once bots start crawling your site."
        />
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6">
      <div className="mb-5">
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 4 }}>TOP PAGES</div>
        {sampleEnabled && <SampleChip />}
        <div style={{ fontFamily: serifFont, fontSize: '18px', color: C.text, letterSpacing: -0.5 }}>
          {sampleEnabled
            ? <>Most bot traffic lands on your <em>product listings</em>.</>
            : <>Most crawled pages this <em>period</em>.</>}
        </div>
      </div>
      <div className="space-y-3">
        {pages.map((p) => {
          const pct = (p.count / maxCount) * 100;
          return (
            <div key={p.page_path} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span
                  style={{ fontFamily: monoFont, fontSize: 11, color: C.text }}
                  className="truncate min-w-0"
                  title={p.page_path} /* title-ok: full value of the truncated path */
                >
                  {p.page_path}
                </span>
                <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, flexShrink: 0 }}>
                  {p.count.toLocaleString()}
                </span>
              </div>
              <div style={{ height: 4, background: C.surface2, borderRadius: 1, overflow: 'hidden' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                  style={{ height: '100%', background: C.skyFg ?? C.muted, opacity: sampleEnabled ? 0.7 : 1 }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// SearchTermsCard — search queries bots submitted to the site. Reads
// `by_search_term` from the breakdowns rollup (ADR-0203). Extracted from
// raw query strings by the API using known search param keys.
// ---------------------------------------------------------------------------

function SearchTermsCard({ range = '7d' }: { range?: RangeId }): JSX.Element {
  const C = useC();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const { activeSite } = useSites();
  const { breakdowns, loading } = useReportingBreakdowns(
    activeSite?.site_id as SiteId | undefined,
    range,
    !sampleEnabled,
  );

  const terms = sampleEnabled
    ? SAMPLE_SEARCH_TERMS
    : (breakdowns?.by_search_term ?? []).slice(0, 10);

  const maxCount = Math.max(1, ...terms.map((t) => t.count));

  const showEmpty = !sampleEnabled && !loading && terms.length === 0;
  if (showEmpty) {
    return (
      <Card>
        <EmptyStateCard
          icon={Search}
          label="Search terms"
          hint="Search terms appear when bots submit queries through your site search."
        />
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6">
      <div className="mb-5">
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 4 }}>BOT SEARCH TERMS</div>
        {sampleEnabled && <SampleChip />}
        <div style={{ fontFamily: serifFont, fontSize: '18px', color: C.text, letterSpacing: -0.5 }}>
          {sampleEnabled
            ? <>Bots are searching for <em>specific products</em> by name.</>
            : <>What bots searched for this <em>period</em>.</>}
        </div>
      </div>
      {/* Weighted chips: size and tint follow the count, so the eye lands on
          what was searched most without another row of progress bars. */}
      <div className="flex flex-wrap gap-2" data-testid="search-term-chips">
        {terms.map((t) => {
          const w = maxCount > 0 ? t.count / maxCount : 0;
          const size = 11 + Math.round(w * 6);
          return (
            <Tooltip key={t.term} content={`${t.count.toLocaleString()} search${t.count === 1 ? '' : 'es'}`} displayMode="inline-flex">
              <span
                className="inline-flex items-baseline gap-1.5"
                style={{
                  fontFamily: sansFont,
                  fontSize: size,
                  color: C.text,
                  padding: '5px 10px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  background: `color-mix(in srgb, ${C.mintFg} ${Math.round(8 + w * 22)}%, ${C.surface2})`,
                  opacity: sampleEnabled ? 0.8 : 1,
                  cursor: 'default',
                }}
              >
                {t.term}
                <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>{t.count.toLocaleString()}</span>
              </span>
            </Tooltip>
          );
        })}
      </div>
    </Card>
  );
}

interface ActivityItem {
  key: string;
  agent: string;
  color: string;
  skill: string;
  amount: string | null;
  /** Pre-rendered ago-stripped relative text — used as the literal
   *  display for the hard-coded SAMPLE_ACTIVITY entries (no source
   *  ISO available) and as the legacy fallback when the iso below is
   *  absent. The row template appends " ago" as literal text. */
  time: string;
  /** ISO instant the row was evaluated at. Present on real records
   *  (the `MandateRecord.evaluated_at` projection); absent on the
   *  hard-coded sample data. When present the row renders a
   *  `<RelativeTime>` atom so the operator gets the full
   *  date+time UTC tooltip on hover (`feat/dry-run-ux-polish` 4). */
  iso?: string;
  status: 'paid' | 'browse' | 'dispute' | 'blocked';
  /**
   * Real `record_id` for the audit row — drives the deep-link
   * (`goTo('mandates', { record })`) and lets a click on the row
   * pre-open the matching detail drawer. Absent on SAMPLE rows
   * (which have no real record behind them; their click degrades
   * to the plain mandates list).
   */
  record_id?: string;
  /**
   * Compact second line of context next to the timestamp. Composed
   * from already-on-the-wire fields so the row carries the same
   * substance as the Mandates card without lighting up an extra
   * fetch: outcome label · settled amount OR block reason. Optional
   * — sample rows omit it so the preview chrome stays stable.
   */
  subline?: string;
  /** Registry id (for the provider mark) when matched or inferred. */
  agent_id?: string | null;
  visitor_class?: 'matched_agent' | 'unknown_agent' | 'human_likely';
  /**
   * Real rows: the record and its server identity verdict
   * (`identityState()`), for the logo rule and the Confirmed / Mismatch
   * marks (visits panel Step A, labels-v2). Absent on SAMPLE rows.
   */
  record?: MandateRecord;
  id_result?: IdentityStateResult;
  geo_country?: string;
  /** What kind of visitor: an AI assistant, a crawler/tool, or a person. */
  visitor_kind?: 'ai' | 'bot' | 'agent' | 'human';
}

// ADR-0115 Slice D — page sizes for the two list widgets. The server caps
// the page at 50 rows (ADR-0010 §3.4); we slice locally to the widget's
// design density (10 / 8) so a busy site doesn't push the cards off-grid.
const ACTIVITY_FEED_LIMIT = 10;
const RECENT_TRANSACTIONS_LIMIT = 8;

/**
 * ADR-0115 Slice D — fetch the latest audit records for the active site
 * through the existing `mandates.listObserved` (= real `audit.list`) bag.
 * Returns a stable, deduped, slice-capped array per call.
 *
 * The widgets share this hook (two consumers, two independent fetches —
 * the server returns the same page in both calls, and Slice D explicitly
 * is "no new endpoint". A composite `/reporting/recent` route is the
 * optional Slice E if the network panel ever shows the double round trip
 * hurts initial paint).
 *
 * Sample-on continues to render the original hard-coded items in the
 * calling widget; this hook is consulted only when sample is off (the
 * widget gates the call so we don't run a network request on the
 * sample-preview path).
 */
function useDashboardRecords(
  site_id: SiteId | undefined,
  limit: number,
  enabled: boolean,
  opts?: { settled?: boolean },
): { records: MandateRecord[]; loading: boolean } {
  const api = useApi();
  const [records, setRecords] = useState<MandateRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // 2026-06 founder feedback — `RecentTransactions` was empty because
  // it filtered the first page client-side and a johnnyvbt site's
  // settled txns sat older than the recent 50. With a server-side
  // `settled=true` filter (added in parallel to this client work),
  // page 1 of the settled set is newest-first AND fully settled —
  // no client-side starvation. We pass `settled: true` through so
  // when the backend lands the widget lights up immediately.
  const settled = opts?.settled === true;

  useEffect(() => {
    if (!enabled || !site_id) {
      setRecords([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const query = settled ? { page: 1, settled: true } : { page: 1 };
    void api.mandates
      .listObserved(site_id, query)
      .then((page) => {
        if (cancelled) return;
        // `MandateRecord` is the wire-shape extension carrying the
        // unsigned `settlement` / `visit_count` siblings. The bag types
        // its return as `AuditRecord[]` to keep the cross-package
        // contract narrow; cast at the boundary so the widget can read
        // `settlement` for the SETTLED filter / amount headline. The
        // structural cast is safe — every `AuditRecord` is a valid
        // `MandateRecord` (the extra fields are optional).
        const all = page.records as MandateRecord[];
        setRecords(all.slice(0, limit));
      })
      .catch(() => {
        // Per ADR-0108 honest empty-state discipline: an audit-list
        // failure renders the empty card, never sample numbers.
        if (!cancelled) setRecords([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, site_id, limit, enabled, settled]);

  return { records, loading };
}

// ADR-0115 Slice D — derive the row label for the agent column. Mirrors
// the Mandates view's `cardAgentId` derivation byte-for-byte: prefer the
// server-classified `identification_match.agent_id`; fall back to a UA-
// inferred id for `unknown_agent` rows; otherwise the visitor is human-
// likely or unclassifiable.
//
// Founder-locked 2026-06-18: returns the BRAND-clean form via
// `shortAgentDisplayName` (e.g. `ChatGPT`, `Claude`). Row cards + the
// Signed Transactions AGENT cell consume this; the longer
// "Org · Product" form stays available through `displayAgentName` for
// the MandateDetail panel where the registry context reads as substance.
function agentLabelForRecord(r: MandateRecord): string {
  const serverAgentId = r.identification_match?.agent_id;
  const visitorClass = r.identification_match?.visitor_class;
  if (serverAgentId !== undefined) {
    return shortAgentDisplayName(serverAgentId);
  }
  if (visitorClass === 'unknown_agent') {
    const inferred = inferAgentFromUserAgent(r.user_agent);
    if (inferred !== null) {
      return shortAgentDisplayName(inferred);
    }
    // The product token the UA carries ("Reflectionbot/1.0", "curl") is
    // an identification — show it. Only a UA with no recognisable token
    // is "Unidentified" (founder 2026-09-23: the feed said "Unidentified
    // agent" for bots the panel already named).
    const token = botNameFromUserAgent(r.user_agent);
    if (token !== null && token.length > 0) return token;
    return 'Unidentified agent';
  }
  if (visitorClass === 'human_likely') {
    // "Visitor from Pembroke Pines" — the visits panel's name (2026-10-13).
    return humanRecordName(r);
  }
  // Verification-rejected path captures `rejected_agent_id` even when
  // identification_match was stripped.
  if (r.rejected_agent_id !== undefined) {
    return shortAgentDisplayName(r.rejected_agent_id);
  }
  return 'Unidentified agent';
}

// ADR-0115 Slice D — derive the dashboard's status pill bucket for a
// record. The Activity feed uses the same four-class taxonomy the
// hard-coded sample used (`paid | browse | dispute | blocked`); we
// preserve it so the existing render branches don't shift.
function statusForActivityRecord(
  r: MandateRecord,
): { status: ActivityItem['status']; amount: string | null } {
  // Rejected mandate (policy block) — render the BLOCKED icon. Covers
  // edge-verifier rejections and origin-side reclassifications.
  if (
    r.decision === 'rejected'
    || r.decision === 'verification_rejected'
    || r.decision === 'rejected_post_verify'
    || r.decision === 'escalated_rejected'
  ) {
    return { status: 'blocked', amount: null };
  }
  // Disputed settlement — render the DISPUTE pill.
  if (r.settlement?.outcome === 'disputed') {
    return { status: 'dispute', amount: null };
  }
  // Settled payment — render the lime $ amount. `settledTotalForHeadline`
  // gates on rail ∈ {stripe, shopify} AND a non-negative integer
  // `total_minor`; everything else falls through to BROWSE.
  const settledAmount = settledTotalForHeadline(r);
  if (settledAmount !== null) {
    return { status: 'paid', amount: settledAmount };
  }
  // Default — observation / browse / read-only.
  return { status: 'browse', amount: null };
}

// ADR-0115 Slice D — relative-time string without the trailing " ago",
// because the row template appends the suffix as literal text.
function timeAgoForRow(iso: string): string {
  return formatRelativeTime(iso).replace(/ ago$/, '');
}

// Title-case the first letter of a single word — used for the outcome
// token in an enriched activity subline ("rejected" → "Rejected").
function titleCase(s: string): string {
  if (s.length === 0) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Founder feedback (2026-06) — build a compact second line of context
 * for a real activity row. Substance comes entirely from fields already
 * on the wire (`MandateRecord`); no extra fetch. Layout:
 *
 *   <Outcome>             — outcomeLabel(decision), title-cased
 *   [· <items>]            — when present and not redundant with outcome
 *   [· $amount]            — only when settled (paid path)
 *   [· <block reason>]     — only on blocked rows, friendly-cased lower
 *
 * Examples:
 *   "Rejected · over budget"
 *   "Approved · 2× ESPRESSO-12OZ · $47.20"
 *   "Observed · Browse catalog"
 *
 * 2026-06-19 — items label added so a merchant sees WHAT was
 * transacted, not just the outcome. `itemsLabelForRecord` (declared
 * later in this file, hoisted) is reused so the Live Activity row and
 * the Signed Transactions ITEMS column derive from the same shape.
 *
 * Returns undefined when neither outcome nor reason is meaningful (a
 * defense-in-depth fallback that keeps the row visually stable).
 */
function searchTermFromQuery(page_query: string | undefined): string | null {
  if (page_query === undefined || page_query.length === 0) return null;
  try {
    const params = new URLSearchParams(page_query.startsWith('?') ? page_query.slice(1) : page_query);
    for (const k of ['q', 's', 'search', 'query', 'keyword', '_search']) {
      const v = params.get(k);
      if (v !== null && v.trim().length > 0) return v.trim().slice(0, 60);
    }
  } catch { /* malformed */ }
  return null;
}

export function activitySublineFromRecord(r: MandateRecord): string {
  const outcome = titleCase(outcomeLabel(r.decision));
  // "Observed" is the default state of a discovery row — saying it on
  // every line was noise (founder 2026-09-23). Only real outcomes lead.
  const parts: string[] = outcome.toLowerCase() === 'observed' ? [] : [outcome];
  // Discovery rows: the page it loaded is the story ("Observed · /pricing").
  // Search pages carry the term. Kept short; the panel has the full list.
  if (typeof r.page_path === 'string' && r.page_path.length > 0 && r.skill_id === undefined && r.intent_summary === undefined) {
    const term = searchTermFromQuery(r.page_query);
    parts.push(term !== null ? `searched “${term}”` : (r.page_path.length > 48 ? `${r.page_path.slice(0, 47)}…` : r.page_path));
  }
  // Items / cart / friendly-action label. Skip it when:
  //   - the items label collapses to the outcome (avoid "Observed · Observed")
  //   - the items label is the generic "Signed mandate" fallback (no
  //     real substance was carried on the wire — keep the row clean)
  const items = itemsLabelForRecord(r);
  if (
    items.length > 0
    && items.toLowerCase() !== outcome.toLowerCase()
    && items !== 'Signed mandate'
  ) {
    parts.push(items);
  }
  // Paid path — surface the actual settled total (the same value the
  // row's amount chip carries) so the subline reads as a full story
  // even when the chip wraps below on a narrow card.
  const settled = settledTotalForHeadline(r);
  if (settled !== null) {
    parts.push(settled);
  }
  // Blocked path — surface the friendly reject reason (the same one
  // the Mandate detail panel renders). Lowercased so it reads as a
  // brief follow-on rather than a second heading.
  if (
    r.decision === 'rejected'
    || r.decision === 'verification_rejected'
    || r.decision === 'rejected_post_verify'
    || r.decision === 'escalated_rejected'
  ) {
    const reason = r.reject_reason;
    if (typeof reason === 'string' && reason.length > 0) {
      parts.push(friendlyRejectReason(reason).toLowerCase());
    }
  }
  return parts.join(' · ');
}

// ADR-0115 Slice D — project a wire `MandateRecord` onto the visual
// `ActivityItem` row shape. Keeps the JSX render branches identical to
// the hard-coded sample path; only the source of the data changes.
function activityItemFromRecord(r: MandateRecord, C: ReturnType<typeof useC>): ActivityItem {
  const { status, amount } = statusForActivityRecord(r);
  const skill = r.skill_id ?? r.intent_summary?.action;
  const skillLabel = skill !== undefined ? friendlyActionLabel(skill) : 'observed';
  const visitor_class = r.identification_match?.visitor_class;
  const inferred_id = r.identification_match?.agent_id ?? (visitor_class === 'unknown_agent' ? inferAgentFromUserAgent(r.user_agent) : null) ?? null;
  // Identity only from the server verdict (labels-v2 Step A). Never
  // cf_verified_bot or a client spoof heuristic.
  const id_result = identityState(r);
  // A record that arrived over a transactional protocol (MCP tool call,
  // A2A, AP2, UCP) is an agent client, not a search crawler — classify
  // it as 'agent' before falling through to 'bot'. The registry-matched
  // AI check still wins when the row is clearly an AI assistant, so a
  // ChatGPT MCP call reads as "AI agent", not "Agent client".
  const protocol = (r as { inbound_protocol?: string }).inbound_protocol;
  const isAgentProtocol = typeof protocol === 'string' && AGENT_CLIENT_PROTOCOLS.has(protocol);
  const visitor_kind: ActivityItem['visitor_kind'] =
    visitor_class === 'human_likely' ? 'human'
      : inferred_id !== null && AI_REGISTRY_RE.test(inferred_id) ? 'ai'
        : isAgentProtocol ? 'agent'
          : inferred_id !== null || visitor_class === 'unknown_agent' ? 'bot'
            : undefined;
  return {
    key: r.record_id,
    agent: id_result.state === 'mismatch' ? quotedName(id_result.name) : agentLabelForRecord(r),
    agent_id: inferred_id,
    ...(visitor_kind !== undefined ? { visitor_kind } : {}),
    ...(visitor_class !== undefined ? { visitor_class } : {}),
    record: r,
    id_result,
    ...(typeof r.geo_country === 'string' ? { geo_country: r.geo_country } : {}),
    // Brand-specific palette is sample-only — for real rows we tone the
    // glyph by classification so a merchant can spot blocks / disputes
    // at a glance. CLAUDE.md §4 — lime for approve, amber for escalate,
    // coral for reject. No new tokens.
    color:
      status === 'blocked'
        ? C.coralFg
        : status === 'dispute'
          ? C.amberFg
          : status === 'paid'
            ? C.limeFg
            : C.muted,
    skill: skillLabel,
    amount,
    time: timeAgoForRow(r.evaluated_at),
    iso: r.evaluated_at,
    status,
    record_id: r.record_id,
    // A human visit leads with "Chrome on Windows" (the panel's line 2).
    subline: [visitor_class === 'human_likely' ? humanRecordDevice(r) : null, activitySublineFromRecord(r)]
      .filter((p): p is string => p !== null && p.length > 0)
      .join(' · '),
  };
}

const SAMPLE_ACTIVITY: ActivityItem[] = [
  { key: 's1', agent: 'ChatGPT', color: '#10A37F', skill: 'place_order', amount: '$47.20', time: '12s', status: 'paid' },
  { key: 's2', agent: 'Perplexity', color: '#20B8CD', skill: 'browse', amount: null, time: '34s', status: 'browse' },
  { key: 's3', agent: 'ChatGPT', color: '#10A37F', skill: 'request_refund', amount: '$22', time: '1m', status: 'dispute' },
  { key: 's4', agent: 'Claude', color: '#CC785C', skill: 'recommend', amount: null, time: '2m', status: 'browse' },
  { key: 's5', agent: 'Unverified', color: '#FF6B47', skill: 'place_order', amount: null, time: '4m', status: 'blocked' },
  { key: 's6', agent: 'ChatGPT', color: '#10A37F', skill: 'subscription', amount: '$32.00', time: '6m', status: 'paid' },
];

/** Registry ids that are AI assistants / answer engines (as opposed to
 *  search crawlers and link previews that also live in the registry). */
const AI_REGISTRY_RE = /^agent_(anthropic_|openai_|google_gemini|perplexity|mistral_|duckduckgo_assistbot|meta_external(agent|fetcher)|apple_extended|commoncrawl)/;
/** Inbound protocols emitted by transactional / tool-calling clients
 *  (ADR-0043, ADR-0215). An `agent_page_hit` beacon from a bot that
 *  happens to run the embed stays on `embed`; a MCP tool-call, A2A
 *  dispatch, AP2 mandate or UCP checkout from an agent surfaces here. */
const AGENT_CLIENT_PROTOCOLS = new Set(['mcp', 'a2a', 'ap2', 'ucp', 'acp']);
const KIND_CHIP: Readonly<Record<'ai' | 'bot' | 'agent' | 'human', { label: string; hint: string }>> = {
  ai: { label: 'AI agent', hint: 'An AI assistant, answer engine or AI training crawler: matched by its documented identity token against Sill\u2019s registry.' },
  bot: { label: 'Crawler', hint: 'A search crawler, SEO tool, link preview or script: named by the token in its user-agent; not an AI assistant.' },
  agent: { label: 'Agent client', hint: 'A tool-calling client reached one of your transactional endpoints (MCP, A2A, AP2, UCP) \u2014 not a search crawler.' },
  human: { label: 'Human', hint: 'A plain browser with no bot token.' },
};

function ActivityFeed({ goTo }: { goTo: SetView }): JSX.Element {
  const C = useC();
  const { activeSite } = useSites();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const empty = useEmptyState();
  // ADR-0115 Slice D — sample-on is a preview path; the hard-coded
  // sample items render unchanged. Sample-off means "real result from
  // audit.list", with the empty card when the list is empty.
  const { records, loading } = useDashboardRecords(
    activeSite?.site_id as SiteId | undefined,
    ACTIVITY_FEED_LIMIT,
    !sampleEnabled,
  );

  const items: ActivityItem[] = sampleEnabled
    ? SAMPLE_ACTIVITY
    : records.map((r) => activityItemFromRecord(r, C));

  // Empty-state fallbacks (preserve existing semantics):
  //   - Fresh site + sample-off → `useEmptyState()` returns true; render
  //     the empty card straight away (avoid a list flash).
  //   - Non-fresh site + sample-off + real list empty → also render the
  //     empty card (the surface stays honest per ADR-0108).
  if (empty || (!sampleEnabled && !loading && items.length === 0)) {
    return (
      <Card>
        <EmptyStateCard icon={Activity} label="Live activity" hint="A live stream of agent events will appear here as soon as your endpoint receives calls." />
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>LIVE ACTIVITY</span>
        {sampleEnabled && <SampleChip />}
        <StatusDot />
      </div>
      <div className="space-y-2">
        {items.map((it, i) => {
          // Founder feedback (2026-06) — clicking a row deep-links to
          // the matching Mandate detail drawer when we have a
          // `record_id` (real rows); falls through to the plain
          // mandates list for SAMPLE rows (no real record behind
          // them). The drawer-open path mirrors `?escalation=` on
          // Resolution: write `?record=…`, view consumes on mount.
          const goRow = (): void => {
            if (it.record_id !== undefined) {
              goTo('mandates', { record: it.record_id });
            } else {
              goTo('mandates');
            }
          };
          return (
          <div
            key={it.key}
            className="flex items-center gap-3 py-2 cursor-pointer"
            style={{ borderBottom: i < items.length - 1 ? `1px solid ${C.borderSoft}` : 'none', paddingBottom: 8 }}
            onClick={goRow}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goRow();
              }
            }}
            aria-label={`${it.agent} — ${it.skill} — open in mandates`}
            data-testid="live-activity-row"
          >
            <div className="flex items-center justify-center" style={{ width: 26, height: 26, background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 2 }}>
              {it.record !== undefined && it.id_result !== undefined ? (
                <VisitLogoGlyph
                  record={it.record}
                  result={it.id_result}
                  size={14}
                  fallbackColor={it.color}
                  {...(it.agent_id === null || it.agent_id === undefined ? { label: it.agent } : {})}
                />
              ) : <Bot size={12} color={it.color} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span style={{ fontFamily: sansFont, fontSize: 12.5, color: C.text, fontWeight: 500 }}>{it.agent}</span>
                {it.record !== undefined && it.id_result !== undefined ? (
                  <VisitIdentityMark record={it.record} result={it.id_result} />
                ) : null}
                {/* A human visit's glyph is already its flag. */}
                {it.geo_country !== undefined && it.visitor_kind !== 'human' ? <CountryFlag code={it.geo_country} /> : null}
                {/* No kind chip on a Mismatch: the kind comes from the
                    claimed registry agent, not this sender. */}
                {it.visitor_kind !== undefined && it.id_result?.state !== 'mismatch' ? (
                  <Tooltip content={KIND_CHIP[it.visitor_kind].hint} displayMode="inline-flex">
                    <Pill tone="neutral">{KIND_CHIP[it.visitor_kind].label}</Pill>
                  </Tooltip>
                ) : null}
                {it.skill !== 'observed' ? <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>{it.skill}</span> : null}
              </div>
              {/* Compact second line — timestamp + outcome / amount /
                  block reason from the wire. Mono kicker for the
                  ago-string keeps the established visual rhythm; the
                  subline reads as a low-key continuation in sans. */}
              <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 2 }}>
                <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted }}>
                  {it.iso !== undefined ? (
                    <RelativeTime iso={it.iso} />
                  ) : (
                    `${it.time} ago`
                  )}
                </span>
                {it.subline !== undefined && it.subline.length > 0 ? (
                  <span
                    style={{
                      fontFamily: sansFont,
                      fontSize: 11,
                      color: C.muted,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    data-testid="live-activity-subline"
                  >
                    · {it.subline}
                  </span>
                ) : null}
              </div>
            </div>
            {it.status === 'paid' && <span style={{ fontFamily: monoFont, fontSize: 11, color: C.limeFg, fontWeight: 500 }}>{it.amount}</span>}
            {it.status === 'browse' && <Eye size={11} color={C.muted} />}
            {it.status === 'blocked' && <Ban size={11} color={C.coralFg} />}
            {it.status === 'dispute' && <Pill tone="amber">DISPUTE</Pill>}
          </div>
          );
        })}
      </div>
    </Card>
  );
}

interface Tx {
  key: string;
  id: string;
  agent: string;
  items: string;
  amount: string;
  /** Pre-rendered ago-stripped relative text — used as the literal
   *  display for the SAMPLE_TXS rows (no source ISO available). */
  time: string;
  /** ISO instant for real records — present on the wired-data path
   *  so the timestamp cell renders `<RelativeTime>` with the full
   *  date+time UTC tooltip on hover. */
  iso?: string;
  proto: string;
  /** 2026-06-19 — refund settlements ARE signed transactions (intent),
   *  but they're money OUT, not money IN. The AMOUNT cell tones in
   *  coral with a `-` prefix and the ITEMS cell carries a "Refund"
   *  pill so a merchant scanning the table reads the refund as a
   *  distinct row class instead of a positive-revenue line. Only set
   *  on the live path; sample rows leave it undefined. */
  isRefund?: boolean;
}

// ADR-0115 Slice D — keep the RecentTransactions filter explicit: ONLY
// records that the rail actually settled. `settledTotalForHeadline`
// returns null for rows without a `settlement.total_minor` on a payment
// rail (Stripe / Shopify), which is the right gate — a discovery
// observation, a verifier rejection, or an approved-but-not-settled
// mandate is not a "signed transaction" in this widget's framing.
function isSettledMandate(r: MandateRecord): boolean {
  return settledTotalForHeadline(r) !== null;
}

// ADR-0115 Slice D — render the cart label for a settled mandate. Mirrors
// the Mandates view's headline derivation: prefer the multi-line cart's
// SKU list, fall back to the single-SKU shape, then the friendly action
// label so the cell is never empty.
function itemsLabelForRecord(r: MandateRecord): string {
  const intent = r.intent_summary;
  if (intent === undefined) {
    const skill = r.skill_id;
    return skill !== undefined ? friendlyActionLabel(skill) : 'Signed mandate';
  }
  if (intent.line_items !== undefined && intent.line_items.length > 0) {
    // "2× Espresso Roast · 1× Cold Brew" — bounded to MAX_LINE_ITEMS
    // upstream, so the join is cheap. Strip undefineds defensively.
    return intent.line_items
      .map((li) => `${li.quantity ?? 1}× ${li.sku}`)
      .join(' · ');
  }
  if (intent.sku !== undefined) {
    const qty = intent.quantity ?? 1;
    return `${qty}× ${intent.sku}`;
  }
  return friendlyActionLabel(intent.action);
}

// Founder-locked 2026-06-18 — the signed protocol_context is NOT on the
// Phase 1 audit-list wire (the field lives on the canonical mandate
// envelope only). The previous heuristic stamped ACP/AP2 from the
// settlement rail — that is an OVERCLAIM (the rail is not the protocol),
// so the column now surfaces the actual SETTLEMENT RAIL — the truthful
// signal a merchant can act on (which executor moved the money). Rows
// with no rail render `—` instead of a guessed protocol.
function railLabelForRecord(r: MandateRecord): string {
  const rail = r.settlement?.rail;
  if (rail === 'stripe') return 'Stripe';
  if (rail === 'shopify') return 'Shopify';
  return '—';
}

// ADR-0115 Slice D — shorten a long ULID record_id to a sample-like
// 8-char tail so the column doesn't break the table grid on a busy
// page. The tooltip still surfaces the full ID via the row's hover.
function shortIdForDisplay(record_id: string, mandate_id: string | undefined): string {
  const id = mandate_id ?? record_id;
  return id.length > 16 ? `${id.slice(0, 12)}…` : id;
}

function RecentTransactions({ goTo }: { goTo: SetView }): JSX.Element {
  const C = useC();
  const { activeSite } = useSites();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const empty = useEmptyState();
  // 2026-06 founder feedback — sample-off now fetches the SETTLED
  // set directly via the new server-side `?settled=true` filter
  // (added in parallel to this client work). The previous design
  // pulled the recent 50 and filtered client-side; merchants with
  // older settled txns ended up with an empty widget because page-1
  // got dominated by discovery observations. With the server filter
  // we can drop the client-side `isSettledMandate` filter — every row
  // page-1 returns is by definition settled.
  //
  // The defense-in-depth client filter is preserved below (the
  // server is the source of truth, but a regression there should
  // never surface a non-settled row in the "Signed transactions"
  // panel).
  const { records, loading } = useDashboardRecords(
    activeSite?.site_id as SiteId | undefined,
    RECENT_TRANSACTIONS_LIMIT,
    !sampleEnabled,
    { settled: true },
  );
  // Defense-in-depth: if the server widens the contract or a stale
  // build returns un-settled rows, this filter still hides them from
  // the "Signed transactions" widget.
  const settled = records.filter(isSettledMandate).slice(0, RECENT_TRANSACTIONS_LIMIT);

  const sampleTxs: Tx[] = [
    { key: 'pm_8a3f9c12', id: 'pm_8a3f9c12', agent: 'ChatGPT', items: '2× Espresso Roast 12oz', amount: '$47.20', time: '12s', proto: 'Stripe' },
    { key: 'pm_7b2e4d31', id: 'pm_7b2e4d31', agent: 'ChatGPT', items: '1× Cold Brew Concentrate', amount: '$22.00', time: '2m', proto: 'Stripe' },
    { key: 'pm_4c9d8f56', id: 'pm_4c9d8f56', agent: 'ChatGPT', items: 'Subscription · Monthly', amount: '$32.00', time: '6m', proto: 'Shopify' },
    { key: 'pm_3a8b1e97', id: 'pm_3a8b1e97', agent: 'Perplexity', items: '3× Single Origin Sampler', amount: '$58.50', time: '8m', proto: 'Stripe' },
    { key: 'pm_1f6a72c4', id: 'pm_1f6a72c4', agent: 'Claude', items: '1× Burr Grinder', amount: '$129.00', time: '14m', proto: 'Stripe' },
  ];

  const txs: Tx[] = sampleEnabled
    ? sampleTxs
    : settled.map((r) => ({
        key: r.record_id,
        id: shortIdForDisplay(r.record_id, r.mandate_id),
        // Founder-locked 2026-06-18: the AGENT cell consumes the
        // richer `agentLabelForRecord` (display map + UA inference +
        // visitor_class + rejected_agent_id), so the cell shows a real
        // BRAND name instead of the literal "AGENT" / SHOUTY_CAPS slug
        // the previous direct `shortAgentDisplayName(agent_id)` would
        // render for records missing a server-classified id.
        agent: agentLabelForRecord(r),
        items: itemsLabelForRecord(r),
        amount: settledTotalForHeadline(r) ?? '—',
        time: timeAgoForRow(r.evaluated_at),
        iso: r.evaluated_at,
        // Founder-locked 2026-06-18: the column surfaces the actual
        // SETTLEMENT RAIL (Stripe / Shopify), not a fabricated protocol
        // stamp. The previous ACP/AP2 heuristic overclaimed (the rail
        // is not the protocol; the wire doesn't carry the signed
        // protocol on the audit-list shape).
        proto: railLabelForRecord(r),
        // 2026-06-19 — refund settlements are signed transactions but
        // they're money OUT, not in. Flag the row so the AMOUNT cell
        // can flip to coral / `-` and the ITEMS cell can carry the
        // refund pill.
        isRefund: r.settlement?.kind === 'refund',
      }));

  if (empty || (!sampleEnabled && !loading && txs.length === 0)) {
    return (
      <Card>
        <EmptyStateCard icon={Receipt} label="Signed transactions" hint="Every settled agent transaction lands here — charges, refunds, and disputes. The vault stays empty until your first payment." />
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>SIGNED TRANSACTIONS</div>
        {sampleEnabled && <SampleChip />}
        <button onClick={() => goTo('mandates')} style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
          Open vault →
        </button>
      </div>
      <div className="overflow-x-auto -mx-4 sm:-mx-6 px-4 sm:px-6">
        <table className="w-full" style={{ minWidth: 500 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {['MANDATE', 'AGENT', 'ITEMS', 'AMOUNT', 'TIME'].map((h) => (
                <th key={h} className="text-left py-3" style={{ fontFamily: monoFont, fontSize: 9, color: C.dim, letterSpacing: 1.5, fontWeight: 400 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {txs.map((tx, i) => (
              <tr key={tx.key} style={{ borderBottom: i < txs.length - 1 ? `1px solid ${C.borderSoft}` : 'none' }} className="cursor-pointer" onClick={() => goTo('mandates', sampleEnabled ? undefined : { record: tx.key })}>
                <td className="py-3">
                  <Tooltip content="Sill-issued payment mandate ID. Click to view the full Intent → Cart → Payment signature chain.">
                    <span className="flex items-center gap-2">
                      <ShieldCheck size={11} color={C.limeFg} />
                      <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>{tx.id}</span>
                    </span>
                  </Tooltip>
                </td>
                <td className="py-3" style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>{tx.agent}</td>
                <td className="py-3" style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>
                  <span className="inline-flex items-center gap-2 flex-wrap">
                    <span>{tx.items}</span>
                    {/* 2026-06-19 — refund settlements get a coral pill
                        next to the items label so the row reads as a
                        distinct class against the lime revenue rows.
                        Non-refund rows render the items label alone
                        (unchanged from the original). */}
                    {tx.isRefund === true ? <Pill tone="coral">Refund</Pill> : null}
                  </span>
                </td>
                <td
                  className="py-3"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 13,
                    // 2026-06-19 — refund rows tone in coral with a
                    // leading `-` so the AMOUNT cell reads as money
                    // out, not in. Non-refund rows stay lime (the
                    // original revenue tone) verbatim.
                    color: tx.isRefund === true ? C.coralFg : C.limeFg,
                    fontWeight: 500,
                  }}
                >
                  {tx.isRefund === true && tx.amount !== '—' && !tx.amount.startsWith('-')
                    ? `-${tx.amount}`
                    : tx.amount}
                </td>
                <td className="py-3" style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                  {tx.iso !== undefined ? <RelativeTime iso={tx.iso} /> : tx.time}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* 2026-06-19 — row-count transparency. The table caps at
          RECENT_TRANSACTIONS_LIMIT (8); when the rendered count
          equals the cap we surface a subtle footer so a merchant
          knows there's more behind it. Live path only (sample rows
          are a fixed five-row preview; no cap-reached signal). */}
      {!sampleEnabled && txs.length === RECENT_TRANSACTIONS_LIMIT ? (
        <button
          type="button"
          onClick={() => goTo('mandates')}
          className="mt-3 block text-left"
          style={{ fontFamily: monoFont, fontSize: 10, color: C.dim, letterSpacing: 1 }}
          data-testid="signed-transactions-overflow"
        >
          Showing latest {RECENT_TRANSACTIONS_LIMIT} · open vault for all
        </button>
      ) : null}
    </Card>
  );
}

interface SkillRow {
  name: string;
  calls: number;
}

// ADR-0115 Slice C — sample-mode top skills (kept verbatim).
const SAMPLE_TOP_SKILLS: SkillRow[] = [
  { name: 'browse_catalog', calls: 1847 },
  { name: 'place_order', calls: 1124 },
  { name: 'check_stock', calls: 894 },
  { name: 'recommend', calls: 612 },
  { name: 'request_refund', calls: 89 },
];

function TopSkills({ range = '7d' }: { range?: RangeId }): JSX.Element {
  const C = useC();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const { activeSite } = useSites();
  const { breakdowns, loading } = useReportingBreakdowns(
    activeSite?.site_id as SiteId | undefined,
    range,
    !sampleEnabled,
  );

  const data: SkillRow[] = sampleEnabled
    ? SAMPLE_TOP_SKILLS
    : (breakdowns?.by_skill ?? []).map((r) => ({ name: r.skill_id, calls: r.calls }));

  const showEmpty = !sampleEnabled && !loading && data.length === 0;
  if (showEmpty) {
    return (
      <Card>
        <EmptyStateCard icon={Activity} label="Top skills" hint="Skill rankings reveal what agents actually do on your site — visible after first traffic." />
      </Card>
    );
  }

  // ADR-0115 Slice B/C must-fix neighbor — same defense as the area
  // chart's maxY guard: `Math.max(1, …)` keeps `0%` widths instead of
  // NaN when the series is empty / all-zero.
  const maxCalls = Math.max(1, ...data.map((d) => d.calls));

  return (
    <Card className="p-4 sm:p-6">
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 4 }}>TOP SKILLS</div>
      {sampleEnabled && <SampleChip />}
      <div style={{ fontFamily: serifFont, fontSize: '18px', color: C.text, marginBottom: 24, letterSpacing: -0.5 }}>
        What agents <em>actually do</em>.
      </div>
      {/* Horizontal bars (no recharts — CLAUDE.md §8).
          2026-06 cohesion pass — each row carries a tooltip so a
          merchant hovering the bar sees a precise "skill_id —
          N calls" readout (the trailing value cell can be visually
          dim at small widths). Subtle hover affordance via background
          shift; no row navigation (the SkillId doesn't have its own
          drill-in view yet — that's an out-of-scope follow-up). */}
      <div className="space-y-3">
        {data.map((d) => (
          <Tooltip
            key={d.name}
            content={`${d.name} — ${d.calls.toLocaleString()} call${d.calls === 1 ? '' : 's'} in window`}
            side="top"
            displayMode="block"
          >
            <div
              className="grid grid-cols-12 items-center gap-3"
              data-testid="top-skills-row"
              style={{ padding: '2px 0' }}
            >
              <span className="col-span-5 sm:col-span-4" style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.name}
              </span>
              <div className="col-span-5 sm:col-span-7">
                <div style={{ height: 8, background: C.surface2, borderRadius: 1, overflow: 'hidden' }}>
                  <motion.div initial={{ width: 0 }} animate={{ width: `${(d.calls / maxCalls) * 100}%` }} transition={{ duration: 1, ease: 'easeOut' }} style={{ height: '100%', background: C.limeFg, borderRadius: 1 }} />
                </div>
              </div>
              <span className="col-span-2 sm:col-span-1" style={{ fontFamily: monoFont, fontSize: 11, color: C.text, textAlign: 'right' }}>{d.calls.toLocaleString()}</span>
            </div>
          </Tooltip>
        ))}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PendingResolutions widget (2026-06 founder feedback)
//
// Summarizes the active site's pending HITL resolutions — `GET
// /v1/escalations?status=pending&site_id=…` (existing endpoint, owned
// by `apps/dashboard/src/api/escalations.ts`). Each summary row deep-
// links to the resolution view; a "Review all →" CTA routes there
// unconditionally so a viewer with permission but no immediate work
// can still navigate.
//
// Empty state: no fetch (no HTTP context, no active site) OR the
// fetch returned zero pending items. The empty card is the honest
// signal ("No pending resolutions.") — never a sample summary.
//
// Cap: 4 rows. The Resolution view is the long-form surface; this
// widget is the at-a-glance count.
// ---------------------------------------------------------------------------

const PENDING_RESOLUTIONS_LIMIT = 4;

interface PendingResolutionsState {
  loading: boolean;
  items: readonly EscalationItem[];
  forbidden: boolean;
  error: boolean;
}

/**
 * Founder feedback (2026-06) — fetches the pending HITL escalations
 * for the active site via the existing `listEscalations` client. No
 * polling on this widget (it's a snapshot; the Resolution view does
 * the live poll). Same gating discipline as the rest of the page —
 * widgets render their empty card on failure / forbidden / no http,
 * never sample numbers.
 */
function usePendingEscalations(
  site_id: string | undefined,
  enabled: boolean,
): PendingResolutionsState {
  const http = useOptionalHttp();
  const [state, setState] = useState<PendingResolutionsState>({
    loading: false,
    items: [],
    forbidden: false,
    error: false,
  });

  useEffect(() => {
    if (!enabled || http === null || site_id === undefined || site_id.length === 0) {
      setState({ loading: false, items: [], forbidden: false, error: false });
      return;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));
    void listEscalations(http, 'pending', site_id).then((result) => {
      if (cancelled) return;
      if (result.kind === 'ok') {
        setState({ loading: false, items: result.items, forbidden: false, error: false });
      } else if (result.kind === 'forbidden') {
        setState({ loading: false, items: [], forbidden: true, error: false });
      } else {
        setState({ loading: false, items: [], forbidden: false, error: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [http, site_id, enabled]);

  return state;
}

/**
 * Friendly summary for a pending escalation row — `merchant · sku`
 * when both are present; degrades to whatever signed-intent field
 * is available. Mirrors the EscalationList summary derivation so the
 * widget reads consistently with the Resolution view.
 */
function escalationSummaryLine(item: EscalationItem): string {
  const s = item.summary;
  const lineItems = s.line_items;
  const skuFragment =
    lineItems !== undefined && lineItems.length > 1
      ? `${lineItems.length} items`
      : lineItems !== undefined && lineItems.length === 1
        ? lineItems[0]!.sku
        : s.sku;
  const head = s.merchant
    + (skuFragment !== undefined ? ` · ${skuFragment}` : '');
  return `${head} · ${friendlyActionLabel(s.action)}`;
}

// Founder feedback (2026-06-18) — sample-mode demo rows for the
// PendingResolutions widget. Mirrors the shape of `EscalationItem`
// (`apps/dashboard/src/api/escalations.ts`) — the row template reads
// `escalation_id`, `created_at`, `summary.{merchant, sku, action,
// failed_rule_id}`. Times sit minutes-to-hours in the past so the
// `RelativeTime` atom renders "12 min ago" — adjusted off the marker
// date so the preview ages naturally with the page.
function buildSamplePendingResolutions(): readonly EscalationItem[] {
  const now = Date.now();
  const minsAgo = (mins: number): string =>
    new Date(now - mins * 60_000).toISOString();
  return [
    {
      escalation_id: 'esc_sample_chatgpt_over_budget',
      mandate_id: 'mnd_sample_chatgpt_over_budget',
      rule_id: 'rule_high_value',
      status: 'pending',
      created_at: minsAgo(12),
      timeout_at: minsAgo(-48),
      summary: {
        action: 'place_order',
        merchant: 'sill-coffee',
        sku: 'ESPRESSO-12OZ',
        max_amount: 175,
        currency: 'USD',
        failed_rule_id: 'r_high_value',
      },
    },
    {
      escalation_id: 'esc_sample_claude_refund',
      mandate_id: 'mnd_sample_claude_refund',
      rule_id: 'rule_refund_review',
      status: 'pending',
      created_at: minsAgo(38),
      timeout_at: minsAgo(-90),
      summary: {
        action: 'request_refund',
        merchant: 'sill-coffee',
        max_amount: 42,
        currency: 'USD',
        reason_code: 'wrong_item',
        scope: 'full',
        original_mandate_id: 'mnd_sample_original_42',
        failed_rule_id: 'r_refund_review',
      },
    },
    {
      escalation_id: 'esc_sample_perplexity_cart',
      mandate_id: 'mnd_sample_perplexity_cart',
      rule_id: 'rule_first_purchase',
      status: 'pending',
      created_at: minsAgo(74),
      timeout_at: minsAgo(-150),
      summary: {
        action: 'place_order',
        merchant: 'sill-coffee',
        line_items: [
          { sku: 'COLD-BREW-32', quantity: 1 },
          { sku: 'OAT-MILK-PK', quantity: 2 },
        ],
        max_amount: 64,
        currency: 'USD',
        failed_rule_id: 'r_first_purchase',
      },
    },
  ];
}
const SAMPLE_PENDING_RESOLUTIONS: readonly EscalationItem[] = buildSamplePendingResolutions();

function PendingResolutions({ goTo }: { goTo: SetView }): JSX.Element {
  const C = useC();
  const { activeSite } = useSites();
  const { enabled: sampleEnabled } = useSampleTraffic();
  // Sample-on path now serves the synthetic rows so the marketing
  // preview shows the surface with substance. Sample-off threads the
  // existing pending-escalations fetch.
  const { loading, items, forbidden, error } = usePendingEscalations(
    activeSite?.site_id as string | undefined,
    !sampleEnabled,
  );

  // Treat forbidden / error as empty paths. The Resolution view will
  // show the precise error (forbidden → "your role cannot view the
  // queue"); on the dashboard the empty card is the right density.
  const effectiveItems: readonly EscalationItem[] = sampleEnabled
    ? SAMPLE_PENDING_RESOLUTIONS
    : items;
  const visible = effectiveItems.slice(0, PENDING_RESOLUTIONS_LIMIT);
  const showEmpty = !sampleEnabled
    && !loading
    && (visible.length === 0 || forbidden || error);

  if (showEmpty) {
    return (
      <Card>
        <EmptyStateCard
          icon={ShieldAlert}
          label="Pending resolutions"
          hint="No pending resolutions."
        />
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>
            PENDING RESOLUTIONS · {effectiveItems.length}
          </div>
          {sampleEnabled && <SampleChip />}
          <div style={{ fontFamily: serifFont, fontSize: '18px', color: C.text, letterSpacing: -0.5, marginTop: 4 }}>
            Held mandates <em>waiting</em> on you.
          </div>
        </div>
        <button
          type="button"
          onClick={() => goTo('resolution')}
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Review all →
        </button>
      </div>
      <div className="space-y-2">
        {visible.map((it, i) => (
          <div
            key={it.escalation_id}
            className="flex items-start gap-3 py-2 cursor-pointer"
            style={{
              borderBottom: i < visible.length - 1 ? `1px solid ${C.borderSoft}` : 'none',
            }}
            onClick={() => goTo('resolution', sampleEnabled ? undefined : { escalation: it.escalation_id })}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goTo('resolution', sampleEnabled ? undefined : { escalation: it.escalation_id });
              }
            }}
            aria-label={`${escalationSummaryLine(it)} — review`}
            data-testid="pending-resolution-row"
          >
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 26,
                height: 26,
                background: C.amberDim,
                border: `1px solid rgba(255,182,39,0.3)`,
                borderRadius: 2,
              }}
            >
              <ShieldAlert size={12} color={C.amberFg} />
            </div>
            <div className="flex-1 min-w-0">
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.text,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {escalationSummaryLine(it)}
              </div>
              <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 2 }}>
                <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted }}>
                  <RelativeTime iso={it.created_at} />
                </span>
                {it.summary.failed_rule_id !== undefined ? (
                  <span style={{ fontFamily: monoFont, fontSize: 10, color: C.amberFg }}>
                    · {it.summary.failed_rule_id}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// BlockedAttacks widget (2026-06 founder feedback)
//
// Reads `breakdowns.by_block_reason: { reason, count }[]` (added in
// parallel to this client work; the typed contract is in
// `packages/types/src/reporting.ts`). Reasons are humanized via
// `friendlyRejectReason` when known; unknown tokens degrade through
// the helper's title-case fallback. A compact bar list mirrors the
// `TopSkills` layout — the value is governance signal density, not
// flair.
//
// Empty state: an empty / undefined `by_block_reason` array OR
// sample-on. The empty card carries the honest "No blocked actions"
// copy.
// ---------------------------------------------------------------------------

const BLOCKED_ATTACKS_LIMIT = 6;

// Founder feedback (2026-06-18) — the BlockedAttacks widget used to
// short-circuit to its empty card on sample-on, which left a heavily
// promoted governance surface blank in the marketing preview. The
// reason tokens are drawn from `friendlyRejectReason`'s closed enum
// (`REJECT_REASON_FRIENDLY` in `intent-label.ts`) so every entry
// humanizes cleanly — `over_budget` → `Over budget`,
// `signature_invalid` → `Invalid signature`, etc. Counts are roughly
// proportional to the SAMPLE_TRAFFIC story.
const SAMPLE_BLOCK_REASONS: ReadonlyArray<{ reason: string; count: number }> = [
  { reason: 'over_budget',     count: 14 },
  { reason: 'agent_unknown',   count: 9 },
  { reason: 'signature_invalid', count: 6 },
  { reason: 'replay_detected', count: 4 },
  { reason: 'sku_not_in_catalog', count: 3 },
  { reason: 'identity_mismatch', count: 2 },
];

function BlockedAttacks({ range = '7d' }: { range?: RangeId }): JSX.Element {
  const C = useC();
  const { enabled: sampleEnabled } = useSampleTraffic();
  const { activeSite } = useSites();
  const { breakdowns, loading } = useReportingBreakdowns(
    activeSite?.site_id as SiteId | undefined,
    range,
    !sampleEnabled,
  );

  // Defensive optional access: the contract declares the field
  // required but a stale server build may not yet emit it. Type the
  // local read as `unknown[]` then narrow at the row.
  // Sample-on path serves the synthetic preview rows so the
  // governance widget renders with substance in the marketing preview.
  const rows: ReadonlyArray<{ reason: string; count: number }> = sampleEnabled
    ? SAMPLE_BLOCK_REASONS
    : ((breakdowns?.by_block_reason ?? []) as ReadonlyArray<{ reason: string; count: number }>);
  const visible = rows.slice(0, BLOCKED_ATTACKS_LIMIT);
  // Empty-card gate: ONLY a real (sample-off) zero-result lands on the
  // empty card now. Sample-on always renders the preview list.
  const showEmpty = !sampleEnabled && !loading && visible.length === 0;

  if (showEmpty) {
    return (
      <Card>
        <EmptyStateCard
          icon={ShieldAlert}
          label="Blocked attacks"
          hint="No blocked actions in this period."
        />
      </Card>
    );
  }

  const maxCount = Math.max(1, ...visible.map((r) => r.count));

  return (
    <Card className="p-4 sm:p-6">
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 4 }}>
        BLOCKED ATTACKS · {range.toUpperCase()}
      </div>
      {sampleEnabled && <SampleChip />}
      <div style={{ fontFamily: serifFont, fontSize: '18px', color: C.text, marginBottom: 24, letterSpacing: -0.5 }}>
        What your <em>guardrails caught</em>.
      </div>
      <div className="space-y-3">
        {visible.map((row) => {
          const label = friendlyRejectReason(row.reason);
          const pct = (row.count / maxCount) * 100;
          return (
            <Tooltip
              key={row.reason}
              content={`${label} — ${row.count.toLocaleString()} blocked attempt${row.count === 1 ? '' : 's'} in window`}
              side="top"
              displayMode="block"
            >
              <div
                className="grid grid-cols-12 items-center gap-3"
                data-testid="blocked-attacks-row"
                style={{ padding: '2px 0' }}
              >
                <span
                  className="col-span-5 sm:col-span-4"
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </span>
                <div className="col-span-5 sm:col-span-7">
                  <div style={{ height: 6, background: C.surface2, borderRadius: 1, overflow: 'hidden' }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      style={{ height: '100%', background: C.coralFg, borderRadius: 1 }}
                    />
                  </div>
                </div>
                <span
                  className="col-span-2 sm:col-span-1"
                  style={{ fontFamily: monoFont, fontSize: 11, color: C.text, textAlign: 'right' }}
                >
                  {row.count.toLocaleString()}
                </span>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </Card>
  );
}
